import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Socket } from "socket.io-client";
import { AgentChat } from "./AgentChat";
import { SessionSelector } from "./SessionSelector";
import { ChatMode } from "./AgentModeDropdown";
import { ModelType, getDefaultModel, sanitizeModel } from "./ModelDropdown";
import { getEngineFromModel } from "./ModelDropdown";
import { Message, MessageType } from "./AgentMessage";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/theme-context";
import { claudeSocketManager } from "@/lib/socket/claudeAgentSocket";
import { getActiveWorkspaceId } from "@/stores/workspace-store";
import { projectsApi } from "@/lib/api/projects";
import { workspacesApi } from "@/lib/api/workspaces";
import { apiClient, createAuthHeaders } from "@/lib/api-client";
import { AlertCircle, WifiOff } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import axios from "axios";
import toast from "react-hot-toast";
import { claudeAgentApi } from "@/lib/api/claude-agent";
import { ErrorRecoveryBanner } from "./ErrorRecoveryBanner";
import { PreviewTestWindow } from "@/components/preview/preview-test-window";
import { usePreviewTestStore } from "@/stores/preview-test-store";
import { useAgentChatStore, TaskStatus } from "@/stores/agentChatStore";
import { useAgentUIStore } from "@/stores/agentUIStore";
import {
  AgentSocketEvent,
  SocketEvent,
  ACCEPT_ATTRIBUTE,
  PlanType,
  sanitizeAskQuestionAnswers,
} from "@optidev/shared-types";
import { billingApi } from "@/lib/api/billing";
import { PublishUpsellModal } from "@/components/billing/PublishUpsellModal";
import {
  ByocExpiredModal,
  type ByocErrorReason,
} from "@/components/byoc/byoc-expired-modal";
import { useFileUpload } from "@/hooks/agent/useFileUpload";
import { useCreditsVerification } from "@/hooks/agent/useCreditsVerification";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { usePermissions } from "@/hooks/usePermissions";
import { usePublish } from "@/hooks/usePublish";
import { extractUIPayload } from "@/lib/agent/payload-parser";
import type { ThemeContent } from "@/types/theme.types";
import { useOnboardingMark } from "@/contexts/onboarding-context";

interface BuilderUploadedFile {
  fileId: string;
  refId: string;
  originalName: string;
  mimeType: string;
  downloadUrl?: string;
  thumbnailUrl?: string;
  fileSize?: number;
}

interface BuilderData {
  fromBuilder: true;
  initialPrompt: string;
  appType: "frontend" | "fullstack" | "unknown";
  tags: string[];
  mode: "plan" | "execute";
  referenceUrls?: string[];
  uploadedFileIds?: string[];
  uploadedFiles?: BuilderUploadedFile[];
  isProcessed?: boolean;
}

// Stable empty array reference to avoid infinite loops in Zustand selectors
// (React's useSyncExternalStore compares by reference, so returning new [] each time causes re-renders)
const EMPTY_MESSAGES: Message[] = [];

// Helper to get placeholder session ID per project
// This prevents race conditions when starting chats from different projects concurrently
const getPlaceholderSessionId = (projectId?: string) =>
  `__NEW_SESSION__${projectId || "unknown"}`;

// Short, friendly text the USER sees in chat after OptiDev Cloud activates.
// The full agent-facing mandate goes via `prompt`; this goes via `displayPrompt`,
// so the technical contract never clutters the conversation.
const OPTIDEV_CLOUD_ACTIVATION_DISPLAY =
  "OptiDev Cloud is ready — continue building.";

// Agent-only continuation prompt pushed the instant OptiDev Cloud finishes
// activating — a deterministic injection point (the agent always sees a pushed
// prompt). Carries the data-access contract + a refactor mandate: agents often
// build the dashboard frontend-first (mock data + setInterval) and then, when
// persistence is added, bolt it on with a direct supabase.from() instead of
// re-routing through Edge Functions. This forces that re-architecture at the
// moment the app goes from mock to real data. Shown to the agent only (paired
// with OPTIDEV_CLOUD_ACTIVATION_DISPLAY via displayPrompt). Keep in sync with
// the optidev-cloud skill (edge-functions/*).
const OPTIDEV_CLOUD_ACTIVATION_PROMPT =
  "[SYSTEM: OptiDev Cloud Activated]\n" +
  "VITE_SUPABASE_GATEWAY_URL and VITE_SUPABASE_PUBLISHABLE_KEY have been injected into .env.local.\n\n" +
  "Before wiring any data, read the optidev-cloud skill: SKILL.md, edge-functions/read-endpoints.md, and edge-functions/polling.md.\n\n" +
  "CONTRACT — all app data access goes through Edge Functions, never direct supabase.from('<table>') (PostgREST), which skips the gateway cache and leaves security to wide-open RLS:\n" +
  "- Read a table → an Edge Function that returns plain JSON, called via supabase.functions.invoke('get-<table>', { method: 'GET' }).\n" +
  "- Write → supabase.functions.invoke('create-<table>', { method: 'POST', body }).\n" +
  "- Auto-refresh / polling → follow polling.md (React Query refetchInterval with jitter); do NOT hand-roll setInterval.\n" +
  "- supabase.storage, supabase.channel (realtime) and supabase.auth are exempt — only table data must go through Edge Functions.\n\n" +
  "REFACTOR EXISTING CODE: if you already built this app with mock data, a hand-rolled setInterval, or any direct supabase.from() call, replace it now with the Edge Function pattern above — do NOT just point existing fetches at supabase.from(). Never leave supabase.from('<table>') in app code.";

interface AgentModeContainerProps {
  devContainerId: string;
  projectId?: string;
  className?: string;
  children?: React.ReactNode;
  headerLeftActions?: React.ReactNode;
  headerRightActions?: React.ReactNode;
  onOpenFile?: (filePath: string) => void;
  onSelectConnections?: () => void;
  onSelectProjectSettings?: () => void;
  onSelectImageGenerator?: () => void;
  onSelectMediaLibrary?: () => void;
  onSelectOptisignsAssets?: () => void;
  onSelectThemes?: () => void;
  pendingAssetRefs?: { assetId: string; name: string }[];
  onPendingAssetRefsConsumed?: () => void;
  /** Push assets picked in an inline HITL card into the composer as @asset chips. */
  onAddAssetRefsToComposer?: (refs: { assetId: string; name: string }[]) => void;
  onOpenHistory?: () => void;
  workspaceId?: string;
  builderData?: BuilderData | null;
  onBuilderDataProcessed?: () => void;
  onStreamingChange?: (isStreaming: boolean) => void;
  onOpenVisualEditor?: () => void;
  mobileActiveTab?: string;
  onMobileActiveTabChange?: (tab: string) => void;
  onOpenSettings?: () => void;
  isContainerReady?: boolean; // Whether the dev container is ready to accept commands
  // External control props (optional - for controlled mode)
  mode?: ChatMode;
  model?: ModelType;
  onModeChange?: (mode: ChatMode) => void;
  onModelChange?: (model: ModelType) => void;
  onStreamComplete?: () => void;
  onThemeGenerated?: (
    theme: ThemeContent,
    action: "preview" | "apply" | "save",
  ) => Promise<void>;
  previewIframe?: HTMLIFrameElement | null;
  previewUrl?: string;
  onEditTheme?: (theme: ThemeContent) => void;
  onThemeSaved?: (themeId: string) => void;
  onBrowseThemes?: () => void;
  onConnectionLinked?: () => void;
  fromApp?: boolean; // True when navigating from the app library after remix
  hideSessionSelector?: boolean; // True to hide the session dropdown (e.g. slides planning page)
  chatContainerClassName?: string; // Extra classes applied to the AgentChat wrapper (e.g. max-w-3xl mx-auto)
  // Called when the user approves a plan. If it returns `{ executePrompt, redirectUrl }`,
  // AgentModeContainer emits the execute prompt on the existing socket (preserving the
  // current sessionId / conversation), marks builderData as processed, and navigates.
  // If it returns void / nothing, the old behavior applies (caller handles everything).
  onPlanApproved?: (
    planFilePath?: string,
  ) =>
    | void
    | Promise<void>
    | { executePrompt: string; redirectUrl: string }
    | Promise<{ executePrompt: string; redirectUrl: string }>;
}

/**
 * Handle exposed by AgentModeContainer via ref
 * Allows parent to imperatively send prompts (e.g., from "Fix Issue" button)
 */
export interface AgentModeContainerHandle {
  sendPrompt: (text: string) => void;
  isConnected: () => boolean;
  isStreaming: () => boolean;
}

export const AgentModeContainer = forwardRef<
  AgentModeContainerHandle,
  AgentModeContainerProps
>(
  (
    {
      devContainerId,
      projectId,
      className,
      children,
      headerLeftActions,
      headerRightActions,
      onOpenFile,
      onSelectConnections,
      onSelectProjectSettings,
      onSelectImageGenerator,
      onSelectMediaLibrary,
      onSelectOptisignsAssets,
      onSelectThemes,
      pendingAssetRefs,
      onPendingAssetRefsConsumed,
      onAddAssetRefsToComposer,
      onOpenHistory,
      workspaceId,
      builderData,
      onBuilderDataProcessed,
      onStreamingChange,
      onOpenVisualEditor,
      mobileActiveTab,
      onMobileActiveTabChange,
      onOpenSettings,
      isContainerReady = true,
      mode: externalMode,
      model: externalModel,
      onModeChange: externalOnModeChange,
      onModelChange: externalOnModelChange,
      onStreamComplete,
      onThemeGenerated,
      previewIframe,
      previewUrl,
      onEditTheme,
      onThemeSaved,
      onBrowseThemes,
      onConnectionLinked,
      fromApp = false,
      hideSessionSelector = false,
      chatContainerClassName,
      onPlanApproved,
    },
    ref,
  ) => {
    const { user, getIdToken } = useAuth();
    const { theme } = useTheme();
    const appNavigate = useAppNavigate();
    const markFirstEdit = useOnboardingMark();

    // Publish hook for shared publish state between HITL card and PublishDropdown
    const { triggerPublish, publishState } = usePublish({ projectId });

    // Free plans are blocked from publishing — the HITL "Publish" card opens this upsell instead.
    // The same modal is reused for the out-of-credits wall; upsellCopy overrides the header
    // (null = default publish-gating copy).
    const [showPublishUpsell, setShowPublishUpsell] = useState(false);
    const [upsellCopy, setUpsellCopy] = useState<{
      title: string;
      description: string;
    } | null>(null);

    // Track last publish status to detect transitions
    const lastPublishStatusRef = useRef(publishState.status);

    // Agent chat store for stale-while-revalidate session caching
    // Get stable method references to avoid re-render issues (calling without selector subscribes to entire store)
    const chatStore = useAgentChatStore.getState(); // Non-reactive access for imperative calls
    const getStoreMessages = useAgentChatStore((state) => state.getMessages);
    const setStoreMessages = useAgentChatStore((state) => state.setMessages);

    // Helper to deduplicate messages by ID and taskId (keeps first occurrence)
    // NOTE: userMessageId links assistant messages to parent user message - multiple
    // assistant messages can share the same userMessageId, so it's NOT for deduplication
    const deduplicateMessages = (msgs: Message[]): Message[] => {
      const seenIds = new Set<string>();
      const seenTaskIds = new Set<string>();

      return msgs.filter((msg) => {
        // Check by id
        if (seenIds.has(msg.id)) {
          console.warn("[AgentMode] Duplicate by id:", msg.id);
          return false;
        }

        // Check by taskId (if present) - applies to ALL message types
        if (msg.taskId && seenTaskIds.has(msg.taskId)) {
          console.warn("[AgentMode] Duplicate by taskId:", msg.taskId);
          return false;
        }

        // Add to seen sets
        seenIds.add(msg.id);
        if (msg.taskId) seenTaskIds.add(msg.taskId);

        return true;
      });
    };

    // Internal state (used when not externally controlled)
    const [internalMode, setInternalMode] = useState<ChatMode>(() => {
      console.log("[AgentMode] Init defaultModel=%s", getDefaultModel());
      return "execute";
    });
    const [internalModel, setInternalModel] =
      useState<ModelType>(getDefaultModel());

    // Use external values if provided, otherwise use internal state.
    // sanitizeModel() guarantees a hidden/unknown model (e.g. the temporarily
    // hidden Gemini) never reaches the agent — it falls back to the default.
    const mode = externalMode !== undefined ? externalMode : internalMode;
    const model = sanitizeModel(
      externalModel !== undefined ? externalModel : internalModel,
    );

    // Handlers that work for both controlled and uncontrolled modes
    const handleModeChange = (newMode: ChatMode) => {
      // Don't allow changes while streaming (isLocalStreaming will be defined later)
      if (externalOnModeChange) {
        externalOnModeChange(newMode);
      } else {
        setInternalMode(newMode);
      }
    };

    const handleModelChange = (newModel: ModelType) => {
      const newEngine = newModel.includes("/") ? "opencode" : "claude";
      console.log(
        "[AgentMode] Model changed: %s → %s (engine: %s → %s)",
        model,
        newModel,
        model?.includes("/") ? "opencode" : "claude",
        newEngine,
      );
      if (externalOnModelChange) {
        externalOnModelChange(newModel);
      } else {
        setInternalModel(newModel);
      }
    };

    // Track if we've loaded the workspace default model
    const hasLoadedWorkspaceDefault = useRef(false);

    // Fetch workspace default model on mount (only for uncontrolled mode)
    useEffect(() => {
      // Skip if externally controlled or already loaded
      if (externalModel !== undefined || hasLoadedWorkspaceDefault.current) {
        return;
      }

      const fetchWorkspaceDefaultModel = async () => {
        try {
          const token = await getIdToken();
          if (!token) return;

          // Use workspaceId prop or get from store
          const wsId = workspaceId || getActiveWorkspaceId();
          if (!wsId) return;

          const response = await workspacesApi.getModelSettings(token, wsId);
          if (response.defaultModel) {
            // A workspace still set to a hidden model (e.g. Gemini) falls back
            // to the default so the picker + agent stay in sync.
            setInternalModel(sanitizeModel(response.defaultModel));
          }
          hasLoadedWorkspaceDefault.current = true;
        } catch (error) {
          // Silently fail - will use system default (sonnet)
          console.debug("Failed to fetch workspace default model:", error);
          hasLoadedWorkspaceDefault.current = true;
        }
      };

      fetchWorkspaceDefaultModel();
    }, [externalModel, workspaceId, getIdToken]);

    const [sessionId, setSessionId] = useState<string | null>(null);

    // Hydrate the Test Window from persisted history so the Tests tab shows on
    // any browser/device whenever this session has test runs (not just from
    // this browser's localStorage). Non-critical: failures are ignored.
    useEffect(() => {
      if (!sessionId) return;
      let cancelled = false;
      (async () => {
        try {
          const token = await getIdToken();
          if (!token || cancelled) return;
          const { runs } = await claudeAgentApi.getPreviewTestRuns(
            token as string,
            sessionId,
          );
          if (!cancelled) usePreviewTestStore.getState().hydrate(runs);
        } catch {
          // Non-critical view data — ignore failures.
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [sessionId, getIdToken]);

    // Placeholder session ID unique per project (prevents race conditions across projects)
    const placeholderSessionId = getPlaceholderSessionId(projectId);

    // Effective session ID: use placeholder when real sessionId is null
    // This allows messages to be stored in Zustand from the start
    const effectiveSessionId = sessionId || placeholderSessionId;

    // Messages from store (single source of truth - always use store now)
    const storeMessages = useAgentChatStore((state) => {
      return (
        state.sessionCache.get(effectiveSessionId)?.messages ?? EMPTY_MESSAGES
      );
    });

    // Active tasks from store (for cross-tab streaming awareness)
    const storeHasActiveTasks = useAgentChatStore((state) => {
      const session = state.sessionCache.get(effectiveSessionId);
      return (session?.activeTasks?.length ?? 0) > 0;
    });

    // Get active task from store (first running/queued task)
    const activeTaskFromStore = useAgentChatStore((state) => {
      const session = state.sessionCache.get(effectiveSessionId);
      return session?.activeTasks?.[0] || null;
    });

    // Derive isStreaming from activeTasks (for store-based streaming detection)
    const isStreamingFromStore = storeHasActiveTasks;

    // Messages always come from store (using effectiveSessionId for placeholder support)
    const messages = storeMessages;

    // Wrapper to update messages (maintains same API as useState setter)
    // Always uses Zustand store with effectiveSessionId (placeholder or real)
    const setMessages = useCallback(
      (updater: Message[] | ((prev: Message[]) => Message[])) => {
        if (typeof updater === "function") {
          const currentMessages = getStoreMessages(effectiveSessionId);
          const newMessages = updater(currentMessages);
          setStoreMessages(effectiveSessionId, newMessages);
        } else {
          setStoreMessages(effectiveSessionId, updater);
        }
      },
      [effectiveSessionId, getStoreMessages, setStoreMessages],
    );

    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLocalStreaming, setIsLocalStreaming] = useState(false);
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [reconnectingTaskId, setReconnectingTaskId] = useState<string | null>(
      null,
    );

    // Socket reconnection state for handling dead connections
    // Note: Socket.io built-in retry (ackTimeout + retries) handles message delivery
    // pendingMessageRef is used for offline queuing only
    const pendingMessageRef = useRef<{
      payload: Record<string, unknown>;
      userMessage: Message;
    } | null>(null);
    const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    const RECONNECT_TIMEOUT_MS = 120000; // 2 minutes (for offline queuing)
    const [sessionRefreshTrigger, setSessionRefreshTrigger] = useState(0);

    // File upload hook (extracted to separate hook for maintainability)
    const {
      attachedFiles,
      setAttachedFiles,
      fileInputRef,
      handleFileInputChange,
      handleFilesDropped,
    } = useFileUpload({
      projectId,
      sessionId,
      devContainerId,
    });

    // Credits and email verification hook (extracted for maintainability)
    const {
      creditBalance,
      allocatedCredits,
      freeDaily,
      isLoadingCredits,
      shownCreditAlerts,
      handleCreditAlertShown,
      fetchCredits,
      resetShownCreditAlerts,
      byocConnected,
      byocEnabled,
      isEmailVerificationBlocked,
      setIsEmailVerificationBlocked,
      isCheckingEmailVerification,
      isResendingVerificationEmail,
      isEmailVerifiedRef,
      handleCheckEmailVerification,
      handleResendVerificationEmail,
    } = useCreditsVerification({
      user,
      getIdToken,
      workspaceId,
    });

    // Open the upgrade modal for the out-of-credits wall, with copy adapted to which
    // wall the free user hit (today / monthly / lifetime). Reuses the publish upsell modal.
    // `reason: "budget"` is the per-turn cap case (a single build hit the free spend backstop).
    const openCreditUpgrade = useCallback((reason: "wall" | "budget" = "wall") => {
      if (reason === "budget") {
        setUpsellCopy({
          title: "Build paused — you've hit your free limit",
          description:
            "This build used up your free credits. Upgrade to keep building.",
        });
        setShowPublishUpsell(true);
        return;
      }
      const wall = freeDaily?.wall ?? "today";
      const copyByWall: Record<string, { title: string; description: string }> = {
        today: {
          title: "You're out of credits for today",
          description:
            "You'll get more free credits tomorrow. Upgrade to keep building right now.",
        },
        monthly: {
          title: "You've used your free credits this month",
          description:
            "Free includes a set number of credits a month. Upgrade to keep building — 100 credits every month, private apps, and custom domains.",
        },
        lifetime: {
          title: "You've used all your free credits",
          description:
            "Upgrade to keep building — 100 credits every month, private apps, and custom domains.",
        },
      };
      setUpsellCopy(copyByWall[wall] ?? copyByWall.today);
      setShowPublishUpsell(true);
    }, [freeDaily]);

    // Paused task state (for recoverable API errors)
    const [pausedTask, setPausedTask] = useState<{
      taskId: string;
      errorCode: string;
      errorMessage: string;
    } | null>(null);

    // Connection lost state (for WebSocket disconnection)
    const [connectionLost, setConnectionLost] = useState(false);

    const socketRef = useRef<Socket | null>(null);
    // Use timestamp-based counter to avoid key collisions across mounts/sessions
    const messageIdCounter = useRef(Date.now());
    const currentStreamingMessageRef = useRef<string | null>(null);
    const activeTaskIdRef = useRef<string | null>(null);
    // Mirrors `isStreaming` for synchronous reads inside handleAction (avoids a
    // stale closure) — used to block HITL action clicks while a task is running.
    const isStreamingRef = useRef(false);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const loadedSessionIdRef = useRef<string | null>(null);
    const projectIdRef = useRef<string | undefined>(projectId);
    const builderDataProcessedRef = useRef(false);
    // Track which sessionId has had pre-session messages migrated to prevent infinite loops
    const migratedSessionIdRef = useRef<string | null>(null);
    // Ref for sessionId to avoid stale closures in socket event handlers
    const sessionIdRef = useRef<string | null>(null);
    // Track whether socket has connected at least once (to distinguish initial connect from reconnection)
    const hasConnectedOnceRef = useRef(false);

    // Keep projectIdRef in sync with projectId prop
    useEffect(() => {
      projectIdRef.current = projectId;
    }, [projectId]);

    // Clear the orphaned placeholder session AFTER the first-message session
    // switch has committed. CHAT_STARTED copies the optimistic messages from the
    // placeholder key to the real sessionId but intentionally leaves the
    // placeholder populated (see note there): clearing it synchronously, while
    // effectiveSessionId still resolves to the placeholder, makes the
    // useSyncExternalStore-backed selector re-render with an empty list (the
    // "blank flicker" on the first message). Once sessionId has flipped to the
    // real id, effectiveSessionId points at the real session, so clearing the
    // placeholder here yields an Object.is-equal snapshot for the rendered
    // session → no re-render, no flicker.
    useEffect(() => {
      if (!sessionId || migratedSessionIdRef.current !== sessionId) return;

      const placeholderId = getPlaceholderSessionId(projectId);
      if (placeholderId === sessionId) return;

      const store = useAgentChatStore.getState();
      if (store.getMessages(placeholderId).length > 0) {
        store.setMessages(placeholderId, []);
        store.clearActiveTasks(placeholderId);
      }
    }, [sessionId, projectId]);

    // Keep sessionIdRef in sync with sessionId state (for stale closure prevention)
    useEffect(() => {
      sessionIdRef.current = sessionId;
    }, [sessionId]);

    // Reset builderDataProcessedRef when projectId changes (new project)
    useEffect(() => {
      builderDataProcessedRef.current = false;
    }, [projectId]);

    // Inject a one-time welcome message when arriving from the app library,
    // but only after the container is ready so the app is actually accessible.
    useEffect(() => {
      if (!fromApp || !isContainerReady || !projectId) return;
      const currentMessages = getStoreMessages(effectiveSessionId);
      if (currentMessages.length > 0) return;
      setStoreMessages(effectiveSessionId, [
        {
          id: `app-welcome-${projectId}`,
          type: MessageType.ASSISTANT,
          content:
            "If anything doesn't look right or you'd like to change something, just ask me here.",
          timestamp: new Date(),
        },
      ]);
    }, [fromApp, isContainerReady, projectId, effectiveSessionId]);

    // === CROSS-PROJECT STATE RESET ===
    // Reset all session state when switching projects to prevent cross-project leakage
    // This ensures effectiveSessionId uses project-specific placeholder until loadActiveSession completes
    const prevProjectIdRef = useRef<string | undefined>(projectId);
    useEffect(() => {
      if (prevProjectIdRef.current && prevProjectIdRef.current !== projectId) {
        console.log("[AgentMode] Project changed, resetting session state");
        // Reset sessionId to use project-specific placeholder (prevents showing old project's chat)
        setSessionId(null);
        // Reset UI streaming state - don't touch localStorage (it's project-scoped)
        setIsLocalStreaming(false);
        setIsReconnecting(false);
        setReconnectingTaskId(null);
        currentStreamingMessageRef.current = null;
        activeTaskIdRef.current = null;
        // Note: Don't clear socket manager's active task - let resumeActiveTasks handle it
        // The task belongs to a different project, so we shouldn't affect it
      }
      prevProjectIdRef.current = projectId;
    }, [projectId]);

    // Watch for publish completion and send continuation to Claude
    // This syncs the HITL publish flow with the agent conversation
    useEffect(() => {
      const prevStatus = lastPublishStatusRef.current;
      const currentStatus = publishState.status;

      // Only act on status transitions
      if (prevStatus === currentStatus) return;
      lastPublishStatusRef.current = currentStatus;

      // Skip if no socket or session
      if (!socketRef.current || !sessionId) return;

      // Handle publish success - send continuation to Claude
      if (currentStatus === "success" && publishState.publicUrl) {
        console.log(
          "[AgentMode] Publish succeeded, sending continuation to Claude",
        );

        socketRef.current.emit("start-claude-session", {
          devContainerId,
          prompt: `The app has been published successfully!\n\nPublic URL: ${publishState.publicUrl}\n\nThe app is now live and accessible to anyone with this link.`,
          mode,
          model,
          sessionId,
          projectId,
          workspaceId,
        });

        setIsLocalStreaming(true);
        // Placeholder is added by the user-message broadcast handler so it
        // renders below the user message (correct order).
      }

      // Handle publish failure - send error continuation to Claude
      if (currentStatus === "failed" && publishState.error) {
        console.log(
          "[AgentMode] Publish failed, sending error continuation to Claude",
        );

        socketRef.current.emit("start-claude-session", {
          devContainerId,
          prompt: `The publish failed with error: ${publishState.error}. Please check for TypeScript errors or build configuration issues.`,
          mode,
          model,
          sessionId,
          projectId,
          workspaceId,
        });

        setIsLocalStreaming(true);
        // Placeholder is added by the user-message broadcast handler so it
        // renders below the user message (correct order).
      }
    }, [
      publishState.status,
      publishState.publicUrl,
      publishState.error,
      sessionId,
      devContainerId,
      mode,
      model,
      projectId,
      workspaceId,
      setMessages,
    ]);

    // Combined streaming state (local + store for cross-tab awareness)
    // Defined early so it can be used in effects and passed to children
    const isStreaming = isLocalStreaming || storeHasActiveTasks;
    isStreamingRef.current = isStreaming;

    // Notify parent component and global store when streaming state changes
    const setGlobalIsAgentStreaming = useAgentUIStore(
      (state) => state.setIsAgentStreaming,
    );
    useEffect(() => {
      if (onStreamingChange) {
        onStreamingChange(isStreaming);
      }
      // Update global store so other components (e.g., AdvisorsTab) can check streaming state
      setGlobalIsAgentStreaming(isStreaming);
    }, [isStreaming, onStreamingChange, setGlobalIsAgentStreaming]);

    // === STORE-DRIVEN SIDE EFFECTS ===
    // These useEffects react to store changes and trigger UI side effects
    // The store is updated by socket handlers in claudeAgentSocket.ts

    // Side effect: Sync streaming state from store to local state
    // This allows other tabs' streaming to update our UI
    useEffect(() => {
      // If store shows streaming but local doesn't, sync the state
      if (
        isStreamingFromStore &&
        !isLocalStreaming &&
        !activeTaskIdRef.current
      ) {
        console.log("[AgentMode] Syncing streaming state from store");
        setIsLocalStreaming(true);
      }
      // If store shows no streaming but we think we're streaming (and no local task), clear
      if (
        !isStreamingFromStore &&
        isLocalStreaming &&
        !activeTaskIdRef.current
      ) {
        console.log("[AgentMode] Clearing streaming state from store sync");
        setIsLocalStreaming(false);
      }
    }, [isStreamingFromStore, isLocalStreaming]);

    // Side effect: Refresh credits and notify parent when streaming completes (store-driven)
    const prevIsStreamingFromStore = useRef(isStreamingFromStore);
    useEffect(() => {
      // Detect transition from streaming -> not streaming
      if (!isStreamingFromStore && prevIsStreamingFromStore.current) {
        console.log(
          "[AgentMode] Streaming completed (store), refreshing credits",
        );
        fetchCredits();
        onStreamComplete?.();
      }
      prevIsStreamingFromStore.current = isStreamingFromStore;
    }, [isStreamingFromStore, fetchCredits, onStreamComplete]);

    // Side effect: Handle session error (insufficient credits, email not verified)
    const sessionError = useAgentChatStore((state) => {
      if (!sessionId) return null;
      return state.sessionCache.get(sessionId)?.error || null;
    });
    useEffect(() => {
      if (!sessionError) return;

      setIsLocalStreaming(false);

      if (sessionError.code === "INSUFFICIENT_CREDITS") {
        fetchCredits();
      } else if (sessionError.code === "BUDGET_CAP_REACHED") {
        // Per-turn free budget backstop tripped — the build ran and its partial output is
        // already shown; open the "build paused — upgrade" wall and refresh the balance.
        openCreditUpgrade("budget");
        fetchCredits();
      } else if (sessionError.code === "EMAIL_NOT_VERIFIED") {
        setIsEmailVerificationBlocked(true);
      } else if (sessionError.code === "CONNECTION_LOST") {
        // Connection lost - set state for banner display (handled by AgentChat)
        setConnectionLost(true);
      } else {
        // Regular error - show error message
        const errorMsg = sessionError.message || "";
        // Ignore user-initiated abort messages - these are expected, not errors
        if (
          errorMsg.toLowerCase().includes("aborted by user") ||
          errorMsg.toLowerCase().includes("process aborted") ||
          errorMsg.toLowerCase().includes("insufficient credits")
        ) {
          return;
        }

        setError(errorMsg || "An error occurred");
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            type: "error",
            content: errorMsg,
            timestamp: new Date(),
          },
        ]);
      }
    }, [sessionError, fetchCredits, sessionId, setMessages, openCreditUpgrade]);

    // Side effect: Handle paused task
    const pausedTaskFromStore = useAgentChatStore((state) => {
      if (!sessionId) return null;
      return state.sessionCache.get(sessionId)?.pausedTask || null;
    });
    useEffect(() => {
      if (pausedTaskFromStore) {
        setPausedTask(pausedTaskFromStore);
        setIsLocalStreaming(false);
        currentStreamingMessageRef.current = null;
      }
    }, [pausedTaskFromStore]);

    // Side effect: Session title updated - refresh session list
    const sessionTitleUpdated = useAgentChatStore((s) => s.sessionTitleUpdated);
    useEffect(() => {
      if (sessionTitleUpdated?.sessionId === sessionId) {
        setSessionRefreshTrigger((prev) => prev + 1);
      }
    }, [sessionTitleUpdated, sessionId]);

    // Side effect: BYOC error - show modal.
    // The backend already disconnected the credential, so the handlers below are UI-only.
    const byocError = useAgentChatStore((s) => s.byocError);
    const clearByocError = useAgentChatStore((s) => s.setByocError);
    const [showByocModal, setShowByocModal] = useState(false);
    // Reconnect lands on Settings → Workspace, a tab only admins/owners can open, so only offer it
    // to them (see `canReconnect` on ByocExpiredModal).
    const { isAdminOrOwner } = usePermissions();

    useEffect(() => {
      if (byocError && byocError.sessionId === sessionId) {
        setShowByocModal(true);
        setIsLocalStreaming(false);
        // Refresh credits since we've been switched to workspace credits
        fetchCredits();
      }
    }, [byocError, sessionId]);

    // Just close modal and navigate to settings - backend already disconnected
    const handleByocReconnect = () => {
      clearByocError(null);
      setShowByocModal(false);
      appNavigate("/dashboard/settings/workspace?claude=reconnect");
    };

    // Just close the modal — `fetchCredits()` above already refreshed the BYOC connected flag too.
    const handleByocContinue = () => {
      clearByocError(null);
      setShowByocModal(false);
    };

    // Side effect: Auto-commit notifications - show toast once then clear
    const latestAutoCommit = useAgentChatStore((s) => s.latestAutoCommit);
    const clearAutoCommit = useAgentChatStore((s) => s.setAutoCommit);
    useEffect(() => {
      if (!latestAutoCommit) return;

      if (latestAutoCommit.status === "created") {
        toast.success("Saved", {
          duration: 2000,
          position: "bottom-right",
          style: {
            background: theme === "dark" ? "#18181b" : "#ffffff",
            color: theme === "dark" ? "#fff" : "#000",
            border:
              theme === "dark" ? "1px solid #27272a" : "1px solid #e5e5e5",
            padding: "8px 16px",
            fontSize: "13px",
          },
        });
      } else if (latestAutoCommit.status === "failed") {
        toast.error("Failed to save", {
          duration: 2000,
          position: "bottom-right",
          style: {
            background: theme === "dark" ? "#18181b" : "#ffffff",
            color: theme === "dark" ? "#fff" : "#000",
            border:
              theme === "dark" ? "1px solid #27272a" : "1px solid #e5e5e5",
            padding: "8px 16px",
            fontSize: "13px",
          },
        });
      }

      // Clear after showing to prevent re-triggering on component remount
      clearAutoCommit(null);
    }, [latestAutoCommit, theme, clearAutoCommit]);

    // Side effect: Email verification block after streaming completes
    const prevIsStreamingForEmailRef = useRef(isStreamingFromStore);
    useEffect(() => {
      // When streaming transitions from true to false
      if (!isStreamingFromStore && prevIsStreamingForEmailRef.current) {
        // Block unverified users after first reply
        if (isEmailVerifiedRef.current === false) {
          console.log(
            "[EMAIL_VERIFY] Blocking unverified user after first reply",
          );
          setIsEmailVerificationBlocked(true);
        }
      }
      prevIsStreamingForEmailRef.current = isStreamingFromStore;
    }, [isStreamingFromStore, setIsEmailVerificationBlocked]);

    // Side effect: Sync refs from store (for places that still need refs)
    // This derives activeTaskIdRef and currentStreamingMessageRef from store state
    useEffect(() => {
      if (activeTaskFromStore) {
        activeTaskIdRef.current = activeTaskFromStore.taskId;
        currentStreamingMessageRef.current = activeTaskFromStore.taskId;
        claudeSocketManager.setActiveTask(activeTaskFromStore.taskId);
      } else {
        activeTaskIdRef.current = null;
        currentStreamingMessageRef.current = null;
        claudeSocketManager.setActiveTask(null);
      }
    }, [activeTaskFromStore]);

    // Side effect: Clear streaming state when active task is removed from store
    // This handles task-cancelled, stream-complete, etc.
    useEffect(() => {
      if (!activeTaskFromStore && isLocalStreaming) {
        console.log(
          "[AgentMode] Active task cleared from store, clearing local streaming state",
        );
        setIsLocalStreaming(false);
        setIsReconnecting(false);
      }
    }, [activeTaskFromStore, isLocalStreaming]);

    // === TASK TRACKING: Cleanup old tasks on mount ===
    useEffect(() => {
      const cleanupOldTasks = () => {
        const now = Date.now();
        const ONE_HOUR = 60 * 60 * 1000;

        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith("streaming-task-")) {
            try {
              const taskData = JSON.parse(localStorage.getItem(key) || "{}");
              const startedAt = new Date(taskData.startedAt).getTime();

              if (now - startedAt > ONE_HOUR) {
                localStorage.removeItem(key);
                console.log("[TASK] Removed old task from localStorage:", key);
              }
            } catch (e) {
              localStorage.removeItem(key);
              console.warn("[TASK] Removed invalid task data:", key);
            }
          }
        });
      };

      cleanupOldTasks();
    }, []);

    // === TASK TRACKING: Update localStorage with assistant message only ===
    // Note: No sessionId dependency - localStorage key is based on taskId, not sessionId
    // This allows updates even when sessionId is null (e.g., during component remount)
    useEffect(() => {
      // Use ref OR socket manager's active task (handles remount race condition)
      // When component remounts, ref is null but socket manager may still have the active task
      const activeTaskId =
        activeTaskIdRef.current || claudeSocketManager.getActiveTask();
      if (!activeTaskId) return;

      const taskKey = `streaming-task-${activeTaskId}`;
      const taskDataStr = localStorage.getItem(taskKey);
      if (taskDataStr) {
        try {
          const taskData = JSON.parse(taskDataStr);

          // Store only the current task's assistant message
          // Use activeTaskId (which may come from socket manager on remount)
          const streamingMsg = messages.find(
            (m) => m.id === activeTaskId && m.type === MessageType.ASSISTANT,
          );
          if (streamingMsg) {
            const existingMsg = taskData.messages?.[0];
            const existingToolCount = existingMsg?.activeTools?.length || 0;
            const newToolCount = streamingMsg.activeTools?.length || 0;
            const existingContentLen = existingMsg?.content?.length || 0;
            const newContentLen = streamingMsg.content?.length || 0;

            // Only save if we have more complete data (more tools OR more content)
            // This prevents a tab with incomplete data from overwriting complete data
            const shouldSave =
              newToolCount >= existingToolCount &&
              newContentLen >= existingContentLen;

            if (shouldSave) {
              // Store the full message object (easier recovery)
              taskData.messages = [
                {
                  id: streamingMsg.id,
                  type: streamingMsg.type,
                  content: streamingMsg.content || "",
                  activeTools: streamingMsg.activeTools || [],
                  toolUsage: streamingMsg.toolUsage, // Include toolUsage for restore
                  timestamp: streamingMsg.timestamp,
                  isLoading: streamingMsg.isLoading,
                  status: streamingMsg.status,
                  userMessageId: streamingMsg.userMessageId,
                  parentEntryId: streamingMsg.parentEntryId,
                },
              ];
              localStorage.setItem(taskKey, JSON.stringify(taskData));
            }
          }
        } catch (e) {
          console.warn("[TASK] Failed to update task streaming content");
        }
      }
    }, [messages]);

    // === TASK TRACKING: Resume active tasks on page load ===
    useEffect(() => {
      const resumeActiveTasks = async () => {
        console.log("[RESUME] resumeActiveTasks called:", {
          user: !!user,
          projectId,
        });
        if (!user || !projectId) return;

        const activeTasks: any[] = [];
        const completedTasks: any[] = [];

        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith("streaming-task-")) {
            try {
              const taskData = JSON.parse(localStorage.getItem(key) || "{}");
              if (taskData.taskId && taskData.projectId === projectId) {
                if (taskData.status === TaskStatus.RUNNING) {
                  activeTasks.push(taskData);
                } else if (taskData.status === TaskStatus.COMPLETED) {
                  completedTasks.push(taskData);
                }
              }
            } catch (e) {
              console.warn("[TASK] Failed to parse task data:", key);
            }
          }
        });

        console.log("[RESUME] Found tasks in localStorage:", {
          activeCount: activeTasks.length,
          completedCount: completedTasks.length,
          activeTasks: activeTasks.map((t) => ({
            taskId: t.taskId?.slice(0, 8),
            status: t.status,
          })),
        });

        if (activeTasks.length === 0) {
          console.log("[TASK] No active streaming tasks.");
          setIsLocalStreaming(false);
          setIsReconnecting(false);
          setReconnectingTaskId(null);
          currentStreamingMessageRef.current = null;
          activeTaskIdRef.current = null;
          claudeSocketManager.setActiveTask(null);

          // If we have completed tasks, still load their conversation to show final content
          if (completedTasks.length > 0) {
            const latestCompleted = completedTasks.sort(
              (a, b) =>
                new Date(b.startedAt).getTime() -
                new Date(a.startedAt).getTime(),
            )[0];

            console.log(
              "[TASK] Loading conversation for completed task:",
              latestCompleted.taskId.slice(0, 8),
            );

            try {
              const token = await getIdToken();
              const historyData = await claudeAgentApi.getConversation(
                token as string,
                latestCompleted.sessionId,
              );
              let restoredMessages = (historyData.messages as Message[]) || [];

              // DEBUG: Log raw API response
              // console.log('[AgentMode] API returned messages:', restoredMessages.map((m: Message) => ({
              //   id: m.id?.slice(0, 8),
              //   type: m.type,
              //   hasActiveTools: !!(m.activeTools?.length),
              //   activeToolsCount: m.activeTools?.length || 0,
              //   hasToolUsage: !!m.toolUsage,
              // })));

              // Overlay cached assistant message with completed status
              const cachedMessage = latestCompleted.messages?.[0];
              if (cachedMessage) {
                const existingIdx = restoredMessages.findIndex(
                  (m: Message) => m.id === latestCompleted.taskId,
                );

                const completedMessage: Message = {
                  id: cachedMessage.id || latestCompleted.taskId,
                  taskId: latestCompleted.taskId,
                  type: MessageType.ASSISTANT,
                  content: cachedMessage.content || "",
                  activeTools: cachedMessage.activeTools || [],
                  timestamp: cachedMessage.timestamp
                    ? new Date(cachedMessage.timestamp)
                    : new Date(latestCompleted.startedAt),
                  isLoading: false,
                  status: TaskStatus.COMPLETED, // Always completed
                  userMessageId:
                    cachedMessage.userMessageId ||
                    latestCompleted.userMessageId ||
                    latestCompleted.parentEntryId,
                };

                if (existingIdx >= 0) {
                  restoredMessages[existingIdx] = {
                    ...restoredMessages[existingIdx],
                    ...completedMessage,
                  };
                } else {
                  restoredMessages.push(completedMessage);
                }
              } else {
                // No cached message - still mark ALL assistant messages as completed
                // since task is completed (prevents showing TaskStatus.RUNNING status)
                restoredMessages = restoredMessages.map((msg: Message) => {
                  if (msg.type === MessageType.ASSISTANT) {
                    return {
                      ...msg,
                      status: TaskStatus.COMPLETED,
                      isLoading: false,
                    };
                  }
                  return msg;
                });
              }

              // DEBUG: Log after processing
              // console.log('[AgentMode] After processing, restoredMessages:', restoredMessages.map((m: Message) => ({
              //   id: m.id?.slice(0, 8),
              //   type: m.type,
              //   hasActiveTools: !!(m.activeTools?.length),
              //   hasToolUsage: !!m.toolUsage,
              // })));

              setMessages(deduplicateMessages(restoredMessages));
              setSessionId(latestCompleted.sessionId);
            } catch (error) {
              console.error(
                "[TASK] Error loading completed task conversation:",
                error,
              );
            }

            // Clean up all completed tasks from localStorage
            completedTasks.forEach((task) => {
              localStorage.removeItem(`streaming-task-${task.taskId}`);
            });
          }
          return;
        }

        activeTasks.sort(
          (a, b) =>
            new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
        );
        const taskToResume = activeTasks[0];

        // Check if Zustand store already has messages for this session
        // If store has data, socket events already updated it - skip localStorage recovery
        // Only use localStorage when Zustand is empty (true browser refresh)
        const storeSession = chatStore.sessionCache.get(taskToResume.sessionId);
        const storeHasMessages =
          storeSession && storeSession.messages.length > 0;

        if (storeHasMessages) {
          console.log(
            "[RESUME] Zustand store already has messages, skipping localStorage recovery",
          );
          // Just sync streaming state from store, don't overwrite with localStorage
          const storeActiveTask = storeSession.activeTasks?.find(
            (t) =>
              t.status === TaskStatus.RUNNING || t.status === TaskStatus.QUEUED,
          );
          if (storeActiveTask) {
            activeTaskIdRef.current = storeActiveTask.taskId;
            claudeSocketManager.setActiveTask(storeActiveTask.taskId);
            setIsLocalStreaming(true);
            setSessionId(taskToResume.sessionId);
          } else {
            // Task completed while we were navigating - just set sessionId
            setSessionId(taskToResume.sessionId);
          }

          // FIX: Only remove localStorage if task is actually completed
          // Check both store (no active task) AND localStorage status
          const taskIsCompleted =
            !storeActiveTask &&
            taskToResume.status !== TaskStatus.RUNNING &&
            taskToResume.status !== TaskStatus.QUEUED;

          if (taskIsCompleted) {
            localStorage.removeItem(`streaming-task-${taskToResume.taskId}`);
            console.log("[RESUME] Cleaned up completed task from localStorage");
          } else {
            console.log(
              "[RESUME] Keeping localStorage - task still running:",
              taskToResume.status,
            );
          }
          return;
        }

        // Only proceed with localStorage recovery if Zustand store is empty (browser refresh)
        console.log(
          "[RESUME] Zustand store empty, recovering from localStorage",
        );

        activeTaskIdRef.current = taskToResume.taskId;
        claudeSocketManager.setActiveTask(taskToResume.taskId);
        setIsReconnecting(true);
        setReconnectingTaskId(taskToResume.taskId);
        setSessionId(taskToResume.sessionId);

        // Load conversation from API, overlay cached message
        // Note: Socket join is AFTER setMessages() to prevent race condition
        try {
          const token = await getIdToken();
          const historyData = await claudeAgentApi.getConversation(
            token as string,
            taskToResume.sessionId,
          );
          let restoredMessages = (historyData.messages as Message[]) || [];
          const apiActiveTasks = historyData.activeTasks || [];

          // Overlay cached assistant message if task has it
          const cachedMessage = taskToResume.messages?.[0];
          if (cachedMessage) {
            // Find existing message by userMessageId (more reliable than taskId which changes to assistantMessageId)
            const cachedUserMessageId =
              cachedMessage.userMessageId ||
              taskToResume.userMessageId ||
              taskToResume.parentEntryId;
            const existingIdx = restoredMessages.findIndex(
              (m: Message) =>
                m.type === MessageType.ASSISTANT &&
                m.userMessageId === cachedUserMessageId,
            );

            const streamingMessage: Message = {
              id: cachedMessage.id || taskToResume.taskId,
              taskId: taskToResume.taskId,
              type: MessageType.ASSISTANT,
              content: cachedMessage.content || "",
              activeTools: cachedMessage.activeTools || [],
              timestamp: cachedMessage.timestamp
                ? new Date(cachedMessage.timestamp)
                : new Date(taskToResume.startedAt),
              isLoading: taskToResume.status === TaskStatus.RUNNING,
              status:
                taskToResume.status === TaskStatus.RUNNING
                  ? TaskStatus.RUNNING
                  : TaskStatus.COMPLETED,
              userMessageId:
                cachedMessage.userMessageId ||
                taskToResume.userMessageId ||
                taskToResume.parentEntryId,
            };

            if (existingIdx >= 0) {
              // Update existing message with cached content (more complete than API)
              restoredMessages[existingIdx] = {
                ...restoredMessages[existingIdx],
                ...streamingMessage,
              };
            } else {
              // Add streaming placeholder if not in history yet
              restoredMessages.push(streamingMessage);
            }
          }

          // === HITL RECOVERY: Restore pending HITL messages from localStorage ===
          // HITL messages are stored temporarily until persisted to DB
          // On page reload, we need to restore them to the conversation
          const hitlKey = `hitl-pending-${taskToResume.taskId}`;
          const hitlDataStr = localStorage.getItem(hitlKey);
          if (hitlDataStr) {
            try {
              const hitlData = JSON.parse(hitlDataStr);
              // Check if HITL message already exists in restored messages
              const hitlExists = restoredMessages.some(
                (m: Message) =>
                  m.content === hitlData.content &&
                  m.type === MessageType.ASSISTANT,
              );
              if (!hitlExists && hitlData.content) {
                console.log(
                  "[TASK] Restoring pending HITL message from localStorage",
                );
                const hitlMessage: Message = {
                  id: `hitl-pending-${taskToResume.taskId}`,
                  type: MessageType.ASSISTANT,
                  content: hitlData.content,
                  timestamp: new Date(hitlData.timestamp),
                  status: TaskStatus.COMPLETED,
                  userMessageId:
                    taskToResume.userMessageId || taskToResume.parentEntryId,
                };
                restoredMessages.push(hitlMessage);
              }
            } catch (e) {
              console.warn("[TASK] Failed to parse HITL data:", hitlKey);
              localStorage.removeItem(hitlKey);
            }
          }

          const finalMessages = deduplicateMessages(restoredMessages);
          setMessages(finalMessages);
          // Join socket AFTER store is updated - handleChatResponse will find the message
          // This prevents race condition where events arrive before store has cached content
          const socket = claudeSocketManager.getSocket();
          if (socket && socket.connected) {
            socket.emit(AgentSocketEvent.JOIN_SESSION, {
              sessionId: taskToResume.sessionId,
            });
          }

          // Priority: Use localStorage status if available, fallback to API activeTasks
          // (localStorage is more up-to-date for same-browser scenarios)
          // API activeTasks helps for cross-browser case where localStorage is empty
          const apiActiveTask = apiActiveTasks.find(
            (t: { taskId: string; status: string; userMessageId: string }) =>
              t.status === TaskStatus.QUEUED || t.status === TaskStatus.RUNNING,
          );
          const taskIsRunning =
            taskToResume.status === TaskStatus.RUNNING ||
            (apiActiveTask &&
              (apiActiveTask.status === TaskStatus.QUEUED ||
                apiActiveTask.status === TaskStatus.RUNNING));
          const activeTaskId = taskToResume.taskId || apiActiveTask?.taskId;

          if (taskIsRunning && activeTaskId) {
            currentStreamingMessageRef.current = activeTaskId;
            activeTaskIdRef.current = activeTaskId;
            claudeSocketManager.setActiveTask(activeTaskId);
            setIsLocalStreaming(true);
          } else {
            setIsLocalStreaming(false);
            currentStreamingMessageRef.current = null;
          }
        } catch (error) {
          console.error(
            "[TASK] Error loading conversation for recovery:",
            error,
          );
        }

        const pollTask = async () => {
          // Early exit if polling was already stopped
          if (!pollingIntervalRef.current) return;

          try {
            const token = await getIdToken();
            if (!token) return;

            const response = await axios.get(
              `${import.meta.env.VITE_API_URL}/api/claude-agent/tasks/${taskToResume.taskId}/status`,
              { headers: { Authorization: `Bearer ${token}` } },
            );

            const { status } = response.data;

            if (status === TaskStatus.COMPLETED || status === "failed") {
              // Remove from localStorage immediately
              localStorage.removeItem(`streaming-task-${taskToResume.taskId}`);

              // Clear interval to stop polling
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }

              setIsReconnecting(false);
              setReconnectingTaskId(null);
              if (status === "failed") {
                setError("Previous task failed to complete");
              }
            }
          } catch (error) {
            console.error("[TASK] Failed to poll task status:", error);
            setIsReconnecting(false);
            setReconnectingTaskId(null);
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
        };

        // Only start polling if task is not already in a terminal state
        if (
          taskToResume.status !== TaskStatus.COMPLETED &&
          taskToResume.status !== "failed"
        ) {
          // Set interval FIRST so pollTask can clear it if needed
          pollingIntervalRef.current = setInterval(pollTask, 5000);
          pollTask();
        } else {
          // Task already completed/failed - just clean up
          setIsReconnecting(false);
          setReconnectingTaskId(null);
          localStorage.removeItem(`streaming-task-${taskToResume.taskId}`);
        }
      };

      resumeActiveTasks();

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }, [user, projectId, getIdToken]);

    // Initialize WebSocket connection
    useEffect(() => {
      if (!user) return;

      const initSocket = async () => {
        try {
          // Pass getIdToken function (not the token value) so socket can fetch fresh token on reconnect
          // Also pass getActiveWorkspaceId to support platform admin accessing other workspaces
          // Pass projectId and sessionId getters for event filtering in critical handlers
          const apiUrl = import.meta.env.VITE_API_URL;
          const socket = claudeSocketManager.connect(
            apiUrl,
            getIdToken,
            getActiveWorkspaceId,
            () => projectIdRef.current,
            () => sessionIdRef.current,
          );

          if (socket.connected) {
            console.log("[AgentMode] Socket already connected on mount");
            setIsConnected(true);
            setError(null);
          }

          // ============================================================
          // SOCKET HANDLER CLEANUP
          // Remove component-level handlers before re-attaching.
          // Critical handlers (store updates) are in claudeAgentSocket.ts
          // ============================================================
          socket.off(SocketEvent.CONNECT);
          socket.off(SocketEvent.DISCONNECT);
          socket.off(SocketEvent.CONNECT_ERROR);
          socket.off(AgentSocketEvent.CONNECTED);
          socket.off(AgentSocketEvent.CHAT_STARTED);

          // NOTE: Critical handlers (store updates, localStorage) are attached
          // automatically by claudeSocketManager.connect() via attachCriticalHandlers()
          // No need to re-attach here - they persist across component mounts

          // ============================================================
          // CONNECTION LIFECYCLE HANDLERS
          // ============================================================

          socket.on(SocketEvent.CONNECT, () => {
            const isReconnection = hasConnectedOnceRef.current;
            hasConnectedOnceRef.current = true;
            console.log(
              "[AgentMode] Connected to Claude agent",
              isReconnection ? "(reconnection)" : "(initial)",
            );
            setIsConnected(true);
            setError(null);
            setIsReconnecting(false);

            // If there's an active task, rejoin the session room
            const activeTask = claudeSocketManager.getActiveTask();
            if (activeTask) {
              const taskKey = `streaming-task-${activeTask}`;
              const taskDataStr = localStorage.getItem(taskKey);
              if (taskDataStr) {
                try {
                  const taskData = JSON.parse(taskDataStr);
                  if (taskData.sessionId) {
                    socket.emit(AgentSocketEvent.JOIN_SESSION, {
                      sessionId: taskData.sessionId,
                    });
                  }
                } catch (e) {
                  // Ignore parse errors
                }
              }
            }

            // On reconnection only: restore session state
            // (Initial connect is handled by loadActiveSession effect + JOIN_SESSION effect)
            if (isReconnection) {
              if (sessionIdRef.current) {
                // Session still in state - just rejoin the room
                socket.emit(AgentSocketEvent.JOIN_SESSION, {
                  sessionId: sessionIdRef.current,
                });
              } else if (!activeTask && projectIdRef.current) {
                // No session in state and no active task - re-fetch from PostgreSQL
                // Handles: backend redeploy, laptop sleep/wake, mobile app resume
                const refetchSession = async () => {
                  try {
                    const token = await getIdToken();
                    if (!token) return;
                    const response = await projectsApi.getActiveSession(
                      token,
                      projectIdRef.current!,
                    );
                    if (response.activeClaudeSessionId) {
                      console.log(
                        "[AgentMode] Reconnect: restored session from PostgreSQL:",
                        response.activeClaudeSessionId,
                      );
                      setSessionId(response.activeClaudeSessionId);
                    }
                  } catch (error) {
                    console.error(
                      "[AgentMode] Reconnect: failed to restore session:",
                      error,
                    );
                  }
                };
                refetchSession();
              }
            }

            // Socket.io built-in retry (ackTimeout + retries) handles message delivery
            // Just clear pending state when connected
            if (pendingMessageRef.current) {
              // Clear the 2-minute timeout since we're now connected
              if (pendingTimeoutRef.current) {
                clearTimeout(pendingTimeoutRef.current);
                pendingTimeoutRef.current = null;
              }
              console.log(
                "[AgentMode] Socket reconnected - clearing pending state",
              );
              pendingMessageRef.current = null;
              setIsReconnecting(false);
            }
          });

          socket.on(SocketEvent.DISCONNECT, (reason) => {
            console.log(
              "[AgentMode] Disconnected from Claude agent, reason:",
              reason,
            );
            setIsConnected(false);

            // If we have a pending message and disconnect wasn't intentional, the reconnect
            // handler will auto-retry. No action needed here.
          });

          socket.on(SocketEvent.CONNECT_ERROR, (error) => {
            console.error("[AgentMode] Connection error:", error.message);
            setIsConnected(false);

            // If we're in the middle of a reconnect attempt for a pending message,
            // the error will trigger another reconnect via Socket.IO's built-in retry.
            // We just need to track the state.
            if (pendingMessageRef.current) {
              console.log(
                "[AgentMode] Connection error during pending message retry",
              );
              // Socket.IO will auto-retry connection, which will trigger 'connect' handler
            }
          });

          socket.on(AgentSocketEvent.CONNECTED, (_data) => {
            // Backend sends { socketId, recovered } - NOT sessionId.
            // Do NOT call setSessionId here - it would clear the active session.
            // Session ID is loaded from PostgreSQL via loadActiveSession or set via CHAT_STARTED.
          });

          socket.on(AgentSocketEvent.CHAT_STARTED, (data) => {
            const newSessionId = data.id || data.sessionId;
            const currentPlaceholderId = getPlaceholderSessionId(projectId);

            // === Rename placeholder session to real sessionId ===
            // With placeholder pattern, messages are always in Zustand store from the start
            // When real sessionId arrives, copy from placeholder → real and clear placeholder
            if (newSessionId && migratedSessionIdRef.current !== newSessionId) {
              const placeholderMessages =
                chatStore.getMessages(currentPlaceholderId);
              const placeholderSession =
                chatStore.getSession(currentPlaceholderId);
              const placeholderTasks = placeholderSession?.activeTasks || [];

              if (
                placeholderMessages.length > 0 ||
                placeholderTasks.length > 0
              ) {
                console.log(
                  "[AgentMode] CHAT_STARTED: Renaming session",
                  currentPlaceholderId,
                  "→",
                  newSessionId,
                  {
                    messageCount: placeholderMessages.length,
                    taskCount: placeholderTasks.length,
                    hasLoadingMsg: placeholderMessages.some((m) => m.isLoading),
                  },
                );
                // Copy placeholder session to real session ID
                const copiedMessages = deduplicateMessages(placeholderMessages);
                chatStore.setMessages(newSessionId, copiedMessages);
                if (placeholderTasks.length > 0) {
                  chatStore.setActiveTasks(newSessionId, placeholderTasks);
                }
                // NOTE: Do NOT clear the placeholder session here. The Zustand
                // selector is backed by useSyncExternalStore, which re-renders
                // SYNCHRONOUSLY on every store mutation. Clearing the placeholder
                // now — while setSessionId() below has not yet committed, so
                // effectiveSessionId still points at the placeholder — forces a
                // render with an empty message list (the first-message "blank
                // flicker"). The placeholder is cleared in the useEffect keyed on
                // [sessionId] below instead, which runs after the session switch
                // commits (effectiveSessionId === newSessionId), making the clear
                // invisible. (The new-chat path also clears it independently.)

                // Debug: Verify copy worked
                const verifyMessages = chatStore.getMessages(newSessionId);
                console.log("[AgentMode] CHAT_STARTED: Verify copy", {
                  copied: copiedMessages.length,
                  verified: verifyMessages.length,
                  hasLoadingMsg: verifyMessages.some((m) => m.isLoading),
                });
              }
              migratedSessionIdRef.current = newSessionId;
            }

            setSessionId(newSessionId);
            setIsLocalStreaming(true);

            if (newSessionId) {
              socket.emit(AgentSocketEvent.JOIN_SESSION, {
                sessionId: newSessionId,
              });

              // Track for same-tab auto-rejoin (sessionStorage = per-tab)
              sessionStorage.setItem(`joined-session-${newSessionId}`, "true");

              // Notify other tabs + enable late-join (localStorage with timestamp for 1h expiry)
              localStorage.setItem(
                "session-joined-notify",
                JSON.stringify({
                  sessionId: newSessionId,
                  timestamp: Date.now(),
                }),
              );

              console.log(
                "[AgentMode] Tracked joined session for auto-rejoin:",
                newSessionId,
              );
            }
          });

          // ============================================================
          // ALL OTHER EVENTS handled by claudeAgentSocket.ts critical handlers:
          // - chat-response: store updates + localStorage
          // - chat-error: store updates (sessionError)
          // - chat-complete: handled via stream-complete
          // - chat-stopped: handled via task-cancelled or stream-complete
          // - stream-complete: store updates + localStorage cleanup
          // - task-created: store updates + localStorage creation
          // - task-cancelled: store updates + localStorage cleanup
          // - session-title-updated: store updates (sessionTitleUpdated)
          // - auto-commit-created/failed: store updates (latestAutoCommit)
          // - hitl-realtime: localStorage for recovery
          // - task-paused: store updates (pausedTask)
          // - tool-started/completed: store updates
          // - hitl-action-clicked/persisted: store updates
          //
          // Component useEffects react to store changes for UI side effects
          // ============================================================

          socketRef.current = socket;
        } catch (error) {
          console.error("[AgentMode] Failed to initialize socket:", error);
        }
      };

      initSocket();

      return () => {
        // Only leave session room if no active task
        // If task is running, keep socket in room to receive streaming events via socket manager
        if (claudeSocketManager.hasActiveTask()) {
          console.log(
            "[AgentMode] Cleanup: Keeping socket in session room (active task in progress)",
          );
          // Don't leave session room - socket manager will continue receiving events
        } else if (socketRef.current && sessionId) {
          socketRef.current.emit(AgentSocketEvent.LEAVE_SESSION, { sessionId });
          console.log("[AgentMode] Cleanup: Left session room:", sessionId);
        }
        claudeSocketManager.disconnect();
      };
    }, [user, getIdToken, sessionId]);

    // Load session from PostgreSQL on mount
    useEffect(() => {
      if (!projectId) return;

      const loadActiveSession = async () => {
        try {
          const token = await getIdToken();
          if (!token) return;

          const response = await projectsApi.getActiveSession(token, projectId);
          if (response.activeClaudeSessionId) {
            setSessionId(response.activeClaudeSessionId);
            console.log(
              "[AgentMode] Restored session from PostgreSQL:",
              response.activeClaudeSessionId,
            );
          } else {
            // Explicitly set null when project has no active session
            // This ensures we use the project-specific placeholder pattern
            setSessionId(null);
            console.log(
              "[AgentMode] No active session for project, using placeholder",
            );
          }
        } catch (error) {
          console.error("[AgentMode] Failed to load active session:", error);
        }
      };

      loadActiveSession();
    }, [devContainerId, projectId, getIdToken]);

    // Load conversation when sessionId is available
    // Use stale-while-revalidate: show cache immediately, then fetch fresh data from server
    // handleSelectSession already implements this pattern internally
    useEffect(() => {
      if (sessionId && loadedSessionIdRef.current !== sessionId) {
        loadedSessionIdRef.current = sessionId;
        const cachedMessages = chatStore.getMessages(sessionId);
        console.log(
          "[AgentMode] Loading conversation for session:",
          sessionId,
          cachedMessages.length > 0
            ? `(${cachedMessages.length} cached, will revalidate)`
            : "(no cache)",
        );
        // Always call handleSelectSession - it shows cache first, then fetches fresh data
        handleSelectSession(sessionId);
      }
    }, [sessionId, chatStore]);

    // Always join session room when sessionId is set and socket is connected
    // This handles: same-tab navigation, cross-tab sync, AND cross-browser/device sync
    // JOIN_SESSION is idempotent (Socket.io ignores if already in room)
    useEffect(() => {
      if (!sessionId || !socketRef.current?.connected) return;

      socketRef.current.emit(AgentSocketEvent.JOIN_SESSION, { sessionId });

      // Track for same-tab (sessionStorage survives navigation within tab)
      sessionStorage.setItem(`joined-session-${sessionId}`, "true");
    }, [sessionId, isConnected]);

    // Cross-tab sync: join room if another tab joined our session (live or within 1 hour)
    useEffect(() => {
      if (!sessionId) return;

      const ONE_HOUR = 60 * 60 * 1000;

      // Helper to check and join if recent session exists
      const checkAndJoin = () => {
        try {
          const stored = localStorage.getItem("session-joined-notify");
          if (stored) {
            const { sessionId: storedSessionId, timestamp } =
              JSON.parse(stored);
            if (
              storedSessionId === sessionId &&
              Date.now() - timestamp < ONE_HOUR
            ) {
              // If socket not connected, connect it first
              if (!socketRef.current?.connected) {
                socketRef.current?.connect();
                return; // Will retry when isConnected changes
              }
              socketRef.current.emit(AgentSocketEvent.JOIN_SESSION, {
                sessionId,
              });
            }
          }
        } catch (e) {
          // Ignore parse errors
        }
      };

      // Check on mount/reconnect
      checkAndJoin();

      // Listen for live changes: when another tab joins our session
      const handleStorageChange = (e: StorageEvent) => {
        if (e.key === "session-joined-notify" && e.newValue) {
          try {
            const { sessionId: newSessionId } = JSON.parse(e.newValue);
            if (newSessionId === sessionId) {
              if (!socketRef.current?.connected) {
                socketRef.current?.connect();
                return;
              }
              socketRef.current.emit(AgentSocketEvent.JOIN_SESSION, {
                sessionId,
              });
            }
          } catch (e) {
            // Ignore parse errors
          }
        }
      };

      window.addEventListener("storage", handleStorageChange);
      return () => window.removeEventListener("storage", handleStorageChange);
    }, [sessionId, isConnected]);

    // Handle builder data initialization
    // Wait for: 1) builderData exists, 2) WebSocket connected
    useEffect(() => {
      if (!builderData || builderData.isProcessed) {
        return;
      }

      // Prevent duplicate processing within same component lifecycle (tab switching)
      if (builderDataProcessedRef.current) {
        console.log(
          "[AgentMode] Builder data already processed in this session, skipping",
        );
        return;
      }

      console.log("[AgentMode] Builder data detected:", builderData);

      if (!isConnected || !socketRef.current) {
        console.log(
          "[AgentMode] Waiting for connection before processing builder data...",
        );
        return;
      }

      // Mark as processed IMMEDIATELY to prevent duplicate execution on remount
      builderDataProcessedRef.current = true;

      // Notify parent to mark builderData as processed (prevents other AgentModeContainer instances from processing)
      if (onBuilderDataProcessed) {
        onBuilderDataProcessed();
      }

      console.log(
        "[AgentMode] Processing builder data and auto-sending prompt",
      );

      const agentMode: ChatMode = builderData.mode;
      handleModeChange(agentMode);

      let formattedPrompt = builderData.initialPrompt;
      if (builderData.referenceUrls && builderData.referenceUrls.length > 0) {
        formattedPrompt += "\n\nRef: " + builderData.referenceUrls.join(", ");
      }

      const filesForMessage = builderData.uploadedFiles?.map((f) => ({
        id: f.fileId,
        refId: f.refId,
        originalName: f.originalName,
        slug: f.originalName.replace(/\.[^/.]+$/, "").toLowerCase(),
        assetUrl: f.downloadUrl || "",
        thumbnailUrl: f.thumbnailUrl,
        downloadUrl: f.downloadUrl,
        fileSize: f.fileSize || 0,
        mimeType: f.mimeType,
        intent: "reference",
      }));
      console.log("[AgentMode] Files for optimistic message:", filesForMessage);

      const optimisticMessage: Message = {
        id: `optimistic-${Date.now()}`,
        type: MessageType.USER,
        content: formattedPrompt,
        timestamp: new Date(),
        files: filesForMessage,
      };
      console.log("[AgentMode] Optimistic message:", optimisticMessage);

      // Loading placeholder so the "Thinking" indicator shows immediately on the
      // first (builder-launched) message, matching the regular send path. The
      // task-created handler swaps it by clientMessageId once the task exists.
      const placeholderMessage: Message = {
        id: `msg-${++messageIdCounter.current}`,
        type: MessageType.ASSISTANT,
        content: "",
        timestamp: new Date(),
        isLoading: true,
      };

      setMessages([optimisticMessage, placeholderMessage]);

      // Clear active session and send prompt
      const sendBuilderPrompt = async () => {
        setSessionId(null);

        if (projectId) {
          try {
            const token = await getIdToken();
            if (token) {
              await projectsApi.updateActiveSession(token, projectId, null);
            }
          } catch (error) {
            console.error("[AgentMode] Failed to clear active session:", error);
          }
        }

        socketRef.current!.emit("start-claude-session", {
          devContainerId,
          prompt: formattedPrompt,
          mode: agentMode,
          model: model,
          sessionId: null,
          projectId: projectId || undefined,
          workspaceId: workspaceId || undefined,
          fileIds: builderData.uploadedFileIds || [],
          clientMessageId: placeholderMessage.id, // swap the loading placeholder on task-created
        });

        setIsLocalStreaming(true);

        // Mark as processed in PostgreSQL (for persistence across page refreshes)
        if (projectId) {
          try {
            const token = await getIdToken();
            if (token) {
              await projectsApi.markBuilderDataProcessed(token, projectId);
              console.log("[AgentMode] Marked builder data as processed");
            }
          } catch (error) {
            console.error(
              "[AgentMode] Failed to mark builder data as processed:",
              error,
            );
          }
        }

        setAttachedFiles([]);
      };

      sendBuilderPrompt();
    }, [
      builderData,
      isConnected,
      model,
      projectId,
      workspaceId,
      devContainerId,
      getIdToken,
    ]);

    // Handle pending agent prompt from localStorage (e.g., from Advisors "Fix with Agent")
    useEffect(() => {
      const stored = localStorage.getItem("pendingAgentPrompt");
      if (!stored) return;

      // Only process if connected
      if (!isConnected || !socketRef.current) {
        console.log(
          "[AgentMode] Waiting for connection before processing pending prompt...",
        );
        return;
      }

      try {
        const { prompt, autoSend, timestamp } = JSON.parse(stored);

        // Expire after 5 minutes
        if (Date.now() - timestamp > 5 * 60 * 1000) {
          console.log("[AgentMode] Pending prompt expired, removing");
          localStorage.removeItem("pendingAgentPrompt");
          return;
        }

        // Remove from localStorage immediately to prevent re-processing
        localStorage.removeItem("pendingAgentPrompt");

        console.log("[AgentMode] Processing pending prompt from localStorage");

        if (autoSend) {
          // Auto-send the prompt
          const userMessageTempId = `msg-${++messageIdCounter.current}`;
          const optimisticMessage: Message = {
            id: userMessageTempId,
            type: MessageType.USER,
            content: prompt,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, optimisticMessage]);

          socketRef.current.emit("start-claude-session", {
            devContainerId,
            prompt,
            mode,
            model,
            sessionId: sessionId || null,
            projectId: projectId || undefined,
            workspaceId: workspaceId || undefined,
          });

          setIsLocalStreaming(true);
        } else {
          // Just pre-fill the input (would need to expose setPrompt via ref or context)
          console.log(
            "[AgentMode] Pending prompt loaded but autoSend is false",
          );
        }
      } catch (error) {
        console.error("[AgentMode] Failed to process pending prompt:", error);
        localStorage.removeItem("pendingAgentPrompt");
      }
    }, [
      isConnected,
      devContainerId,
      mode,
      model,
      sessionId,
      projectId,
      workspaceId,
    ]);

    // Save session to PostgreSQL when it changes
    useEffect(() => {
      if (!projectId || !sessionId) return;

      const saveActiveSession = async () => {
        try {
          const token = await getIdToken();
          if (!token) return;

          await projectsApi.updateActiveSession(token, projectId, sessionId);
          console.log("[AgentMode] Saved session to PostgreSQL:", sessionId);
        } catch (error) {
          console.error("[AgentMode] Failed to save active session:", error);
        }
      };

      saveActiveSession();
    }, [sessionId, projectId, getIdToken]);

    // === HANDLERS ===
    const handleSendPrompt = async (prompt: string) => {
      // Check socket exists
      if (!socketRef.current) {
        setError("Not connected to Claude agent");
        return;
      }

      const currentSessionId = sessionId;
      console.log(
        "[AgentMode] Sending message with sessionId:",
        currentSessionId,
      );

      // Mark all previous approve_plan messages as stale (user is sending a new message)
      setMessages((prev) =>
        prev.map((msg) => {
          if (
            msg.type === MessageType.ASSISTANT &&
            msg.content.includes('"hitl_type":"approve_plan"')
          ) {
            return { ...msg, isStale: true };
          }
          return msg;
        }),
      );

      let uploadedFileIds: string[] | undefined = undefined;

      // Generate user message id first (needed for payload)
      const userMessageTempId = `msg-${++messageIdCounter.current}`;

      // If files are attached, extract file IDs
      if (attachedFiles.length > 0) {
        uploadedFileIds = attachedFiles
          .map((f) => f.fileId)
          .filter(Boolean) as string[];
        console.log("[AgentMode] Attached file IDs:", uploadedFileIds);

        // Add user message WITH uploaded file thumbnails
        const userMessage: Message = {
          id: userMessageTempId,
          type: MessageType.USER,
          content: prompt,
          timestamp: new Date(),
          files: attachedFiles.map((f) => ({
            id: f.fileId || "",
            refId: f.refId || "",
            originalName: f.file.name,
            slug: f.file.name.replace(/\.[^/.]+$/, "").toLowerCase(),
            assetUrl: f.downloadUrl || "",
            thumbnailUrl: f.thumbnailUrl,
            downloadUrl: f.downloadUrl,
            fileSize: f.file.size,
            mimeType: f.file.type,
            intent: "use-in-app",
          })),
        };

        setMessages((prev) => [...prev, userMessage]);
        setAttachedFiles([]);
      } else {
        // Add user message without files
        const userMessage: Message = {
          id: userMessageTempId,
          type: MessageType.USER,
          content: prompt,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);
      }

      setError(null);
      setIsLocalStreaming(true);

      // Mark onboarding step 2 on first execute mode send
      if (mode === "execute") {
        markFirstEdit();
      }

      // Add placeholder assistant message with loading state
      const placeholderMessage: Message = {
        id: `msg-${++messageIdCounter.current}`,
        type: MessageType.ASSISTANT,
        content: "",
        timestamp: new Date(),
        isLoading: true,
      };
      setMessages((prev) => [...prev, placeholderMessage]);

      // Build payload for emit (and potential retry)
      // requestId ensures exactly-once task creation via server-side deduplication
      // clientMessageId allows exact matching of assistant placeholder on task-created
      // userMessageTempId allows matching user message on user-message event
      const payload = {
        requestId: crypto.randomUUID(), // Idempotency key for Socket.io retry deduplication
        clientMessageId: placeholderMessage.id, // For matching assistant placeholder on task-created
        userMessageTempId, // For matching user message on user-message event
        workspaceId,
        prompt: prompt,
        mode,
        model: model,
        sessionId: currentSessionId,
        projectId: projectId || undefined,
        devContainerId: devContainerId || undefined,
        fileIds: uploadedFileIds,
      };

      // Get the user message we just added (for retry restoration)
      const lastUserMessage = messages[messages.length - 1];

      // If socket is disconnected, queue message for auto-retry on reconnect
      // User doesn't need to know - message will auto-send when connection restores
      if (!socketRef.current.connected) {
        console.log(
          "[AgentMode] Socket disconnected - queuing message for auto-retry on reconnect",
        );
        pendingMessageRef.current = { payload, userMessage: lastUserMessage };
        setIsReconnecting(true);

        // Start 2-minute timeout - if no reconnect by then, show error
        pendingTimeoutRef.current = setTimeout(() => {
          if (pendingMessageRef.current) {
            console.error("[AgentMode] Reconnect timeout after 2 minutes");
            setIsLocalStreaming(false);
            setIsReconnecting(false);
            setMessages((prev) => prev.filter((msg) => !msg.isLoading));
            setError(
              "Message could not be delivered. Please check your connection.",
            );
            pendingMessageRef.current = null;
          }
        }, RECONNECT_TIMEOUT_MS);

        // Nudge reconnection (Socket.io may already be reconnecting)
        socketRef.current.connect();
        return;
      }

      // Send to backend with Socket.io built-in retry (ackTimeout + retries from socket config)
      // If all retries fail, show error. Server-side deduplication via requestId prevents duplicate tasks.
      console.log("[AgentMode] Emitting start-claude-session", {
        connected: socketRef.current.connected,
        requestId: payload.requestId,
        prompt: (payload.prompt as string)?.substring(0, 30),
      });

      socketRef.current.emit(
        "start-claude-session",
        payload,
        (
          err: Error | null,
          ack:
            | { success: boolean; error?: string; duplicate?: boolean }
            | undefined,
        ) => {
          console.log("[AgentMode] Emit callback received:", {
            err: err?.message,
            ack,
          });
          if (err) {
            // All 5 retries exhausted - no ack received
            console.error(
              "[AgentMode] Message could not be delivered after retries:",
              err,
            );
            setIsLocalStreaming(false);
            setIsReconnecting(false);
            setMessages((prev) => prev.filter((msg) => !msg.isLoading));
            setError(
              "Message could not be delivered. Please check your connection.",
            );
            pendingMessageRef.current = null;
            return;
          }

          // Ack received - check if server accepted the message
          if (ack?.duplicate) {
            // Retry delivered but task already exists (server-side dedup worked)
            console.log(
              "[AgentMode] Retry delivered - task already exists:",
              ack,
            );
          } else if (ack && !ack.success) {
            // Server received but rejected (business logic error)
            // Don't retry - let chat-error event handle UI
            console.warn("[AgentMode] Server rejected message:", ack.error);
          } else {
            // Success
            console.log("[AgentMode] Message delivered successfully");
            pendingMessageRef.current = null;
          }
        },
      );
    };

    // Expose methods to parent via ref (for "Fix Issue" button etc.)
    useImperativeHandle(
      ref,
      () => ({
        sendPrompt: (text: string) => {
          if (!isConnected) {
            console.warn("[AgentMode] Cannot send prompt - not connected");
            return;
          }
          if (isStreaming) {
            console.warn("[AgentMode] Cannot send prompt - already streaming");
            return;
          }
          handleSendPrompt(text);
        },
        isConnected: () => isConnected,
        isStreaming: () => isStreaming,
      }),
      [isConnected, isStreaming, handleSendPrompt],
    );

    const handleStopChat = () => {
      if (socketRef.current && sessionId) {
        const taskId =
          currentStreamingMessageRef.current || activeTaskIdRef.current;
        console.log(
          "[AgentMode] Stopping chat for session:",
          sessionId,
          "taskId:",
          taskId,
        );
        socketRef.current.emit("stop-chat", { sessionId, taskId });

        if (taskId) {
          setMessages((prev) =>
            prev.map((msg) =>
              // Match by id OR taskId (msg.id may change to entryId after assistant-entry-saved)
              msg.id === taskId || msg.taskId === taskId
                ? {
                    ...msg,
                    status: TaskStatus.COMPLETED,
                    // Mark the halt so the phase timeline doesn't peel the
                    // interrupted phase into a "Wrap-up" / "Over to you" node
                    // (status is COMPLETED on stop, so this flag is the only
                    // signal that it wasn't a natural finish).
                    stopped: true,
                    // Mark all running tools as completed when stopping
                    activeTools: msg.activeTools?.map((tool) =>
                      tool.status === "running"
                        ? {
                            ...tool,
                            status: "completed" as const,
                            completedAt: new Date(),
                          }
                        : tool,
                    ),
                  }
                : msg,
            ),
          );

          // Update localStorage immediately as fallback (don't wait for server event)
          const taskKey = `streaming-task-${taskId}`;
          const taskDataStr = localStorage.getItem(taskKey);
          if (taskDataStr) {
            try {
              const taskData = JSON.parse(taskDataStr);
              taskData.status = "cancelled";
              taskData.completedAt = new Date().toISOString();
              localStorage.setItem(taskKey, JSON.stringify(taskData));
              console.log(
                "[AgentMode] Updated localStorage on stop (fallback)",
              );
            } catch (e) {
              console.warn(
                "[AgentMode] Failed to update localStorage on stop:",
                e,
              );
            }
          }
        } else {
          setMessages((prev) => prev.filter((msg) => !msg.isLoading));
        }

        setIsLocalStreaming(false);
        currentStreamingMessageRef.current = null;
        activeTaskIdRef.current = null;
        claudeSocketManager.setActiveTask(null);
      }
    };

    const handleSelectSession = async (newSessionId: string | null) => {
      // Leave current session room before switching (prevents cross-session event leakage)
      if (socketRef.current && sessionId) {
        socketRef.current.emit("leave-session", { sessionId });
        console.log("[AgentMode] Left session room:", sessionId);
      }

      // Clear streaming state to prevent stale task references
      activeTaskIdRef.current = null;
      currentStreamingMessageRef.current = null;
      claudeSocketManager.setActiveTask(null);

      // Reset shown credit alerts so they show again when switching sessions
      resetShownCreditAlerts();

      if (newSessionId) {
        // Switching to existing session
        console.log("[AgentMode] Loading session history:", newSessionId);

        // === CRITICAL: Check localStorage for active streaming task BEFORE setting sessionId ===
        // This prevents race condition where socket joins before API returns activeTask
        // Socket events for unknown tasks are filtered out, so we need activeTaskIdRef set first
        const findActiveTask = (): {
          taskId: string;
          sessionId: string;
        } | null => {
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            if (key.startsWith("streaming-task-")) {
              try {
                const taskData = JSON.parse(localStorage.getItem(key) || "{}");
                if (
                  taskData.sessionId === newSessionId &&
                  taskData.status === TaskStatus.RUNNING
                ) {
                  return {
                    taskId: taskData.taskId,
                    sessionId: taskData.sessionId,
                  };
                }
              } catch (e) {
                // Ignore parse errors
              }
            }
          }
          return null;
        };
        const localStorageActiveTask = findActiveTask();

        // Set activeTaskIdRef BEFORE sessionId changes trigger socket join
        if (localStorageActiveTask) {
          activeTaskIdRef.current = localStorageActiveTask.taskId;
          claudeSocketManager.setActiveTask(localStorageActiveTask.taskId);
          console.log(
            "[AgentMode] Pre-set activeTask from localStorage:",
            localStorageActiveTask.taskId.slice(0, 8),
          );
        }

        // Now safe to set sessionId (triggers socket join via useEffect)
        setSessionId(newSessionId);
        chatStore.switchSession(newSessionId);

        // === STALE-WHILE-REVALIDATE ===
        // Check if session is cached in store (persists across navigation)
        // Declare outside block so it's available in error handlers
        const hasCachedData = chatStore.hasSessionData(newSessionId);
        const cachedSession = hasCachedData
          ? chatStore.getSession(newSessionId)
          : undefined;

        if (hasCachedData) {
          // STALE: Show cached messages immediately (already in store, component will re-render)
          console.log(
            "[AgentMode] Showing cached session from store:",
            chatStore.getMessages(newSessionId).length,
            "messages",
          );

          // Check if there's an active task to resume (find first running/queued task)
          const cachedActiveTask = cachedSession?.activeTasks?.find(
            (t) =>
              t.status === TaskStatus.RUNNING || t.status === TaskStatus.QUEUED,
          );
          if (cachedActiveTask) {
            activeTaskIdRef.current = cachedActiveTask.taskId;
            claudeSocketManager.setActiveTask(cachedActiveTask.taskId);
            setIsLocalStreaming(true);
            setIsReconnecting(true);
            setReconnectingTaskId(cachedActiveTask.taskId);
            console.log(
              "[AgentMode] Resuming active task from store cache:",
              cachedActiveTask.taskId,
            );
          }
        } else {
          // No cache - show loading state (use store directly since sessionId closure isn't updated yet)
          chatStore.setMessages(newSessionId, [
            {
              id: "loading",
              type: MessageType.ASSISTANT,
              content: "Loading previous conversation...",
              timestamp: new Date(),
            } as Message,
          ]);
        }

        // REVALIDATE: Fetch fresh data from API (runs in background if cached)
        try {
          const token = await getIdToken();
          const data = await claudeAgentApi.getConversation(
            token as string,
            newSessionId,
          );
          const freshMessages = (data.messages as Message[]) || [];
          const activeTasks = data.activeTasks || [];

          // Update store with fresh data - server is source of truth
          // Clear placeholder session when selecting existing session
          chatStore.setMessages(placeholderSessionId, []);
          chatStore.clearActiveTasks(placeholderSessionId);
          migratedSessionIdRef.current = newSessionId;

          // === STALE-WHILE-REVALIDATE MERGE ===
          // Use initSessionWithMerge which properly handles:
          // - RUNNING tasks → LOCAL first (has streaming progress)
          // - COMPLETED tasks → API (authoritative)
          // - Deduplication by ID, taskId, and userMessageId
          chatStore.initSessionWithMerge(
            newSessionId,
            freshMessages,
            activeTasks,
          );

          // Handle cleanup when no active tasks
          if (activeTasks.length === 0) {
            // Clear any stale activeTasks from cache when server has no active tasks
            chatStore.clearActiveTasks(newSessionId);
            // Also clear streaming states that may have been set during stale phase
            setIsLocalStreaming(false);
            setIsReconnecting(false);
            setReconnectingTaskId(null);
            activeTaskIdRef.current = null;
            claudeSocketManager.setActiveTask(null);
            // Clean up any completed localStorage entries for this session
            // FIX: Only remove if task is actually completed, not still running
            const keys = Object.keys(localStorage);
            for (const key of keys) {
              if (key.startsWith("streaming-task-")) {
                try {
                  const taskData = JSON.parse(
                    localStorage.getItem(key) || "{}",
                  );
                  // Only remove if sessionId matches AND task is not running
                  if (
                    taskData.sessionId === newSessionId &&
                    taskData.status !== TaskStatus.RUNNING &&
                    taskData.status !== TaskStatus.QUEUED
                  ) {
                    console.log(
                      "[AgentMode] Cleaning up completed localStorage entry:",
                      key,
                    );
                    localStorage.removeItem(key);
                  }
                } catch (e) {
                  // Ignore parse errors
                }
              }
            }
          }

          if (freshMessages.length > 0) {
            console.log(
              "[AgentMode] Revalidated with",
              freshMessages.length,
              "messages from API",
            );

            // Handle active task from API response (use first running/queued task for streaming)
            const primaryActiveTask = activeTasks.find(
              (t: { taskId: string; status: string; userMessageId: string }) =>
                t.status === TaskStatus.QUEUED ||
                t.status === TaskStatus.RUNNING,
            );
            if (primaryActiveTask) {
              activeTaskIdRef.current = primaryActiveTask.taskId;
              claudeSocketManager.setActiveTask(primaryActiveTask.taskId);
              setIsLocalStreaming(true);
              setIsReconnecting(true);
              setReconnectingTaskId(primaryActiveTask.taskId);
            }
          } else {
            console.log("[AgentMode] No messages found in session");
          }
        } catch (error) {
          console.error(
            "[AgentMode] Error loading conversation history:",
            error,
          );
          // If we had cached data, keep showing it; otherwise show error
          if (!cachedSession) {
            chatStore.setMessages(newSessionId, [
              {
                id: "error",
                type: "error",
                content:
                  "Failed to load previous conversation. Starting fresh.",
                timestamp: new Date(),
              } as Message,
            ]);
          }
        }
      } else {
        // Starting new session - clear placeholder session in Zustand
        setSessionId(null);
        chatStore.setMessages(placeholderSessionId, []);
        chatStore.clearActiveTasks(placeholderSessionId);
        // Note: No need to clear session tracking - we track per-session, not per-project
        // Old session's tracking remains, so switching back to it will auto-rejoin

        // Refresh credits to show latest balance
        fetchCredits();

        // Reset shown credit alerts so they show again in new chat
        resetShownCreditAlerts();

        // Clear active session in PostgreSQL
        if (projectId) {
          try {
            const token = await getIdToken();
            if (token) {
              await projectsApi.updateActiveSession(token, projectId, null);
            }
          } catch (error) {
            console.error("[AgentMode] Failed to clear active session:", error);
          }
        }

        console.log("[AgentMode] Starting new session");
      }
    };

    // HITL Action dispatcher - handles action button clicks from ClaudeResponseCard
    const handleAction = useCallback(
      async (actionId: string, messageId: string) => {
        console.log(
          "[AgentMode] Action triggered:",
          actionId,
          "messageId:",
          messageId,
        );

        // Block HITL action clicks while a task is already running — otherwise
        // clicking a second card (or a stale one) fires two simultaneous turns.
        // The input is blocked for the same reason; the buttons must be too.
        // (Identical duplicate cards are already deduped server- and client-side.)
        if (isStreamingRef.current) {
          toast("Please wait for the current step to finish.");
          return;
        }

        // === DISABLE BUTTON IMMEDIATELY (optimistic UI) ===
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  clickedActions: [...(msg.clickedActions || []), actionId],
                }
              : msg,
          ),
        );

        // Helper to persist clicked action to backend (best-effort, non-blocking)
        const persistClickedAction = () => {
          if (!messageId) {
            console.warn("[AgentMode] No messageId, skipping persist");
            return;
          }
          getIdToken().then((token) => {
            if (token) {
              axios
                .patch(
                  `${import.meta.env.VITE_API_URL}/api/claude-agent/messages/${messageId}/clicked-action`,
                  { actionId },
                  { headers: { Authorization: `Bearer ${token}` } },
                )
                .catch((err) => {
                  console.warn(
                    "[AgentMode] Failed to persist clicked action:",
                    err,
                  );
                });
            }
          });
        };

        // Persist in background (non-blocking)
        persistClickedAction();

        // Handle select_media action (same pattern as activate_optidev_cloud)
        // Sends continuation message to agent with selected media details
        if (actionId.startsWith("select_media:")) {
          const jsonPayload = actionId.substring("select_media:".length);
          try {
            const selectedItems = JSON.parse(jsonPayload) as Array<{
              provider: string;
              mediaId: string;
              type: string;
              title: string;
              downloadUrl: string;
            }>;

            if (!socketRef.current || !sessionId) {
              toast.error("No active session");
              return;
            }
            const itemList = selectedItems
              .map(
                (item, i) =>
                  `${i + 1}. "${item.title}" (provider: ${item.provider}, id: ${item.mediaId}, type: ${item.type})`,
              )
              .join("\n");

            const mediaType = selectedItems[0]?.type || "image";
            const displayPrompt = `Use the ${selectedItems.length} selected ${mediaType === "audio" ? "audio files" : mediaType === "video" ? "videos" : "images"}`;

            socketRef.current.emit("start-claude-session", {
              devContainerId,
              prompt: `The user has selected ${selectedItems.length} media file(s). Download each one using optidev_download_media and continue:\n\n${itemList}`,
              displayPrompt,
              mode,
              model,
              sessionId,
              projectId,
              workspaceId,
            });

            setIsLocalStreaming(true);
            // Don't add a placeholder here — the user-message broadcast arrives
            // after this click and will add both the user message and the
            // placeholder in the correct order (user msg first, then placeholder).
          } catch (err) {
            console.error(
              "[AgentMode] Failed to parse select_media payload:",
              err,
            );
            toast.error("Failed to send media selection to agent");
          }
          return;
        }

        // Handle OptiSigns asset selection from an inline HITL card. Instead of
        // injecting a new turn (which could land mid-execution), drop the picks
        // into the composer as @asset chips — the user reviews and sends, and the
        // gateway resolves the chips with the optisigns-assets skill on send.
        // (Folder browsing now happens in-card via direct fetch — no agent turn.)
        if (actionId.startsWith("add_optisigns_assets:")) {
          const jsonPayload = actionId.substring(
            "add_optisigns_assets:".length,
          );
          try {
            const refs = JSON.parse(jsonPayload) as {
              assetId: string;
              name: string;
            }[];
            onAddAssetRefsToComposer?.(refs);
          } catch (err) {
            console.error(
              "[AgentMode] Failed to parse add_optisigns_assets payload:",
              err,
            );
            toast.error("Failed to add OptiSigns assets to your message");
          }
          return;
        }

        // Handle ask-user-question answers (fire-and-forget). The card was shown
        // and the agent ended its turn; feeding the answers back as a continuation
        // message resumes it in a fresh turn (correct ordering, no tool timeout).
        // Answers are smuggled through the actionId (same pattern as select_media);
        // the optimistic clickedActions push above already collapsed the card to
        // its answered summary, so this reads as a seamless "answer → continue".
        // OptiSigns destructive-write confirm (delete asset/schedule/playlist item,
        // unpair a screen, assign live content). The card built by the sidecar carries
        // the exact op + args; on Confirm we re-invoke the agent to call that tool with
        // `confirmed: true`. This is the ONLY path that runs the write — the tool refused
        // the first, unconfirmed call. Cancel is a pure client-side dismiss (no handler).
        if (actionId.startsWith("optisigns_write_confirm:")) {
          const jsonPayload = actionId.substring(
            "optisigns_write_confirm:".length,
          );
          try {
            const pending = JSON.parse(jsonPayload) as {
              op: string;
              args: Record<string, unknown>;
              assetName: string;
              screenName: string;
            };
            if (!socketRef.current || !sessionId) {
              toast.error("No active session");
              return;
            }
            // Assign carries asset/screen names; generic destructive confirms
            // (delete/remove/unpair) don't — keep the prompt action-neutral then.
            const isAssign = Boolean(pending.assetName || pending.screenName);
            const prompt = isAssign
              ? `The user confirmed the OptiSigns assignment. Execute it now: call the \`${pending.op}\` tool with \`confirmed: true\` and these arguments: ${JSON.stringify(
                  pending.args,
                )}. This assigns "${pending.assetName}" to the screen "${pending.screenName}". Report the true outcome (live / submitted for approval / blocked).`
              : `The user confirmed the OptiSigns action. Execute it now: call the \`${pending.op}\` tool with \`confirmed: true\` and these arguments: ${JSON.stringify(
                  pending.args,
                )}. Report the true outcome (live / submitted for approval / blocked).`;

            socketRef.current.emit("start-claude-session", {
              devContainerId,
              prompt,
              displayPrompt: isAssign
                ? `Confirmed — assign "${pending.assetName}" to "${pending.screenName}"`
                : `Confirmed — run ${pending.op.replace(/^optisigns_/, "").replace(/_/g, " ")}`,
              mode,
              model,
              sessionId,
              projectId,
              workspaceId,
            });
            setIsLocalStreaming(true);
          } catch (err) {
            console.error(
              "[AgentMode] Failed to parse optisigns_write_confirm payload:",
              err,
            );
            toast.error("Failed to confirm the OptiSigns action");
          }
          return;
        }

        if (actionId.startsWith("ask_question_answer:")) {
          const jsonPayload = actionId.substring("ask_question_answer:".length);
          try {
            const parsed = JSON.parse(jsonPayload) as {
              requestId: string;
              answers: Record<string, string[]>;
              skipped?: boolean;
            };
            if (!socketRef.current || !sessionId) {
              toast.error("No active session");
              return;
            }

            // Sanitize before the answers enter the agent's context / build steps.
            const answers = parsed.skipped
              ? {}
              : sanitizeAskQuestionAnswers(parsed.answers);
            const entries = Object.entries(answers);

            let prompt: string;
            let displayPrompt: string;
            if (parsed.skipped || entries.length === 0) {
              prompt =
                "I'll skip the questions — please proceed with sensible defaults and briefly state any assumptions you make.";
              displayPrompt = "Skipped the questions";
            } else {
              const lines = entries
                .map(([header, values]) => `- **${header}:** ${values.join(", ")}`)
                .join("\n");
              prompt = `Here are my answers:\n\n${lines}\n\nPlease continue with these.`;
              displayPrompt = "Answered your questions";
            }

            socketRef.current.emit("start-claude-session", {
              devContainerId,
              prompt,
              displayPrompt,
              mode,
              model,
              sessionId,
              projectId,
              workspaceId,
            });
            setIsLocalStreaming(true);
          } catch (err) {
            console.error(
              "[AgentMode] Failed to parse ask_question_answer payload:",
              err,
            );
            toast.error("Failed to send your answers to the agent");
          }
          return;
        }

        switch (actionId) {
          case "activate_optidev_cloud": {
            if (!projectId) {
              toast.error("No project context available");
              return;
            }

            // Morph the activate card IN PLACE into a staged progress card —
            // set `activation` on the same HITL message so there's no second
            // card and the original card's clicked chip never shows. Fall back
            // to a fresh message only if we somehow have no messageId.
            const activationMsgId =
              messageId || `msg-${++messageIdCounter.current}`;
            const setActivation = (
              activation: "provisioning" | "ready" | "failed" | "timeout",
            ) =>
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === activationMsgId ? { ...m, activation } : m,
                ),
              );
            if (messageId) {
              setActivation("provisioning");
            } else {
              setMessages((prev) => [
                ...prev,
                {
                  id: activationMsgId,
                  type: "system",
                  content: "",
                  activation: "provisioning",
                  timestamp: new Date(),
                },
              ]);
            }

            // Resume the agent EXACTLY ONCE when ready. Poll callbacks can
            // overlap (the awaited GET can outlast the 2s interval), so without
            // this latch the status flipping to ACTIVE_HEALTHY could fire the
            // continuation twice → two injected messages.
            let settled = false;
            const finishReady = () => {
              if (settled) return;
              settled = true;
              setActivation("ready");
              toast.success("OptiDev Cloud activated!");
              // Let the progress bar fill to full green (the card ticks any
              // remaining stages to done) BEFORE injecting the continuation, so
              // the user sees completion before the agent resumes. The delay
              // safely covers the worst-case completion animation.
              setTimeout(() => {
                if (socketRef.current && sessionId) {
                  socketRef.current.emit("start-claude-session", {
                    devContainerId,
                    prompt: OPTIDEV_CLOUD_ACTIVATION_PROMPT,
                    displayPrompt: OPTIDEV_CLOUD_ACTIVATION_DISPLAY,
                    mode,
                    model,
                    sessionId,
                    projectId,
                    workspaceId,
                  });
                  setIsLocalStreaming(true);
                }
              }, 2000);
            };

            try {
              const token = await getIdToken();
              if (!token) {
                setActivation("failed");
                toast.error("Authentication required");
                return;
              }

              const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/api/supabase/projects/${projectId}/activate`,
                {},
                { headers: { Authorization: `Bearer ${token}` } },
              );
              const status = response.data.config?.status;

              if (status === "ACTIVE_HEALTHY") {
                // Warm pool: instant activation.
                finishReady();
              } else if (status === "PROVISIONING") {
                // Direct provision: poll until ready (card self-animates stages).
                let timeoutId: ReturnType<typeof setTimeout>;
                const pollInterval = setInterval(async () => {
                  if (settled) {
                    clearInterval(pollInterval);
                    return;
                  }
                  try {
                    const statusResponse = await axios.get(
                      `${import.meta.env.VITE_API_URL}/api/supabase/projects/${projectId}/status`,
                      { headers: { Authorization: `Bearer ${token}` } },
                    );
                    if (statusResponse.data.status === "ACTIVE_HEALTHY") {
                      clearInterval(pollInterval);
                      clearTimeout(timeoutId);
                      finishReady();
                    } else if (statusResponse.data.status === "FAILED") {
                      if (settled) return;
                      settled = true;
                      clearInterval(pollInterval);
                      clearTimeout(timeoutId);
                      setActivation("failed");
                      toast.error("OptiDev Cloud activation failed");
                    }
                  } catch (pollError) {
                    console.error("[AgentMode] Polling error:", pollError);
                  }
                }, 2000);

                // Give up after 3 minutes.
                timeoutId = setTimeout(() => {
                  if (settled) return;
                  settled = true;
                  clearInterval(pollInterval);
                  setActivation("timeout");
                }, 180000);
              } else {
                setActivation("failed");
                toast.error("Unknown activation status");
              }
            } catch (error: any) {
              console.error(
                "[AgentMode] OptiDev Cloud activation failed:",
                error,
              );
              setActivation("failed");
              toast.error(
                error.response?.data?.message ||
                  "Failed to activate OptiDev Cloud",
              );
            }
            break;
          }

          case "enable_cross_project_sharing": {
            // Enable cross-project sharing on current project, then retry
            console.log("[AgentMode] Enabling cross-project sharing");
            try {
              const token = await getIdToken();
              if (!token) throw new Error("Not authenticated");

              const workspaceIdHeader = getActiveWorkspaceId();
              await fetch(
                `${import.meta.env.VITE_API_URL}/api/editor/projects/${projectId}`,
                {
                  method: "PUT",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    ...(workspaceIdHeader && {
                      "X-Workspace-Id": workspaceIdHeader,
                    }),
                  },
                  body: JSON.stringify({ allowCrossReference: true }),
                },
              );
              toast.success("Cross-project sharing enabled");

              // Send continuation so Claude retries the operation
              if (socketRef.current && sessionId) {
                setIsLocalStreaming(true);
                // Placeholder is added by the user-message broadcast handler
                // so it renders below the user message (correct order).

                socketRef.current.emit("start-claude-session", {
                  devContainerId,
                  prompt:
                    "Cross-project sharing has been enabled by the user. Please retry the previous cross-project operation.",
                  mode,
                  model,
                  sessionId,
                  projectId,
                  workspaceId,
                });
              }
            } catch (err: any) {
              toast.error("Failed to enable cross-project sharing");
              console.error(
                "[AgentMode] Enable cross-project sharing failed:",
                err,
              );
            }
            break;
          }

          case "go_to_workspace_settings": {
            // Navigate to workspace settings page
            window.open("/dashboard/settings/workspace", "_blank");
            break;
          }

          case "approve_plan": {
            // Plan approved - if in plan mode, switch to execute mode and continue
            // If already in execute mode, just continue with implementation
            console.log("[AgentMode] Plan approved", { currentMode: mode });

            // If a redirect handler is provided (e.g. slides page), use it instead of in-place execute
            if (onPlanApproved) {
              onPlanApproved();
              return;
            }

            if (!socketRef.current || !sessionId) {
              toast.error("No active session");
              return;
            }

            // If in plan mode, switch to execute mode
            if (mode === "plan") {
              handleModeChange("execute");
            }

            setIsLocalStreaming(true);

            socketRef.current.emit("start-claude-session", {
              devContainerId,
              prompt:
                "The user has approved the plan. Proceed with implementation.",
              mode: "execute", // Always use execute mode for implementation
              model,
              sessionId,
              projectId,
              workspaceId,
            });
            // Placeholder is added by the user-message broadcast handler so
            // it renders below the user message (correct order).

            break;
          }

          case "preview_theme":
          case "apply_theme": {
            console.log("[AgentMode] Theme action triggered:", actionId);

            // Find the message with the theme context
            const message = messages.find((msg) => msg.id === messageId);
            if (!message) {
              toast.error("Message not found");
              return;
            }

            // Extract UI payload from message content
            const parsedResponse = extractUIPayload(message.content);
            if (
              !parsedResponse.payload ||
              !parsedResponse.payload.themeContext
            ) {
              toast.error("Theme data not found in message");
              return;
            }

            const themeContext = parsedResponse.payload.themeContext;

            // Transform ThemeContext to ThemeContent
            const theme = {
              name: themeContext.name,
              type: "registry:theme",
              cssVars: JSON.parse(themeContext.content).cssVars,
              colors: themeContext.colors,
            };

            // Call the onThemeGenerated callback
            const action = actionId === "preview_theme" ? "preview" : "apply";
            try {
              await onThemeGenerated?.(theme, action);
              console.log("theme action completed:", theme, action);
              if (actionId === "apply_theme") {
                toast.success(`Theme "${theme.name}" applied successfully`);
              }
            } catch (error: any) {
              console.error("[AgentMode] Theme action failed:", error);
              toast.error(error.message || `Failed to ${action} theme`);
            }
            break;
          }

          case "setup_connection_cancelled": {
            console.log("[AgentMode] Connection setup cancelled by user");

            if (!socketRef.current || !sessionId) {
              console.warn(
                "[AgentMode] No socket or session for cancellation continuation",
              );
              break;
            }

            // Notify agent that user skipped connection setup
            socketRef.current.emit("start-claude-session", {
              devContainerId,
              prompt:
                "The user has skipped the connection setup. Please continue without the connection or ask the user how they would like to proceed.",
              mode,
              model,
              sessionId,
              projectId,
              workspaceId,
            });

            setIsLocalStreaming(true);
            // Placeholder is added by the user-message broadcast handler so
            // it renders below the user message (correct order).
            break;
          }

          case "publish_project": {
            if (!projectId) {
              toast.error("No project context available");
              return;
            }

            // Free plans can't publish — show the upsell modal instead of building.
            // Backend also enforces this (403), so a failed plan lookup falls through safely.
            try {
              const token = await getIdToken();
              if (token) {
                const subscription = await billingApi.getSubscription(token);
                if (subscription.planType === PlanType.FREE) {
                  setUpsellCopy(null); // default publish-gating copy
                  setShowPublishUpsell(true);
                  return;
                }
              }
            } catch (err) {
              console.error(
                "[AgentMode] Failed to check plan before publish:",
                err,
              );
            }

            // Use the shared publish hook - this updates the store which syncs
            // with both the HITL card and PublishDropdown
            console.log("[AgentMode] Triggering publish via shared hook");
            triggerPublish();
            break;
          }

          case "start_building": {
            // User clicked Start Building on a propose_project_plan_hitl card.
            // Resolve the plan file path from the payload, let the parent supply the
            // execute prompt + redirect URL (when available), then send the execute
            // message on the EXISTING socket so the plan conversation is preserved.
            console.log("[AgentMode] Start Building clicked", { messageId });

            const message = messages.find((msg) => msg.id === messageId);
            if (!message) return;

            const parsed = extractUIPayload(message.content);
            const firstPlan = parsed.payload?.planContext?.plans?.[0];
            if (!firstPlan) return;

            const result = onPlanApproved
              ? await onPlanApproved(firstPlan.filePath)
              : {
                  executePrompt: `Read the plan file at ${firstPlan.filePath} and execute it end-to-end.`,
                  redirectUrl: "",
                };
            if (
              !result ||
              typeof result !== "object" ||
              !("executePrompt" in result)
            ) {
              // Parent opted out of the session-preserving path — nothing to do here.
              return;
            }

            if (!socketRef.current || !sessionId || !devContainerId) {
              toast.error(
                "Cannot continue session: WebSocket or session not ready",
              );
              return;
            }

            // Switch the local mode to execute so subsequent UI matches
            handleModeChange("execute");
            setIsLocalStreaming(true);
            // Placeholder is added by the user-message broadcast handler so
            // it renders below the user message (correct order).

            // Emit to the EXISTING session (Claude SDK resumes the same conversation).
            // `prompt` is the long execute instruction that only Claude sees.
            // `displayPrompt` is the short user-visible chip (the full prompt as a
            // chat bubble is noise — the user just clicked Start Building).
            socketRef.current.emit("start-claude-session", {
              devContainerId,
              prompt: result.executePrompt,
              displayPrompt: "Start building",
              mode: "execute",
              model,
              sessionId,
              projectId,
              workspaceId,
            });

            // Mark builderData processed so Power Editor skips its auto-launch
            // (which would otherwise clear messages + create a new session).
            (async () => {
              try {
                const token = await getIdToken();
                if (token && projectId) {
                  await projectsApi.markBuilderDataProcessed(token, projectId);
                }
              } catch (err) {
                console.warn(
                  "[AgentMode] Failed to mark builderData processed",
                  err,
                );
              }
              if (result.redirectUrl) {
                window.location.href = result.redirectUrl;
              }
            })();

            break;
          }

          default:
            console.warn("[AgentMode] Unknown action:", actionId);
        }
      },
      [
        projectId,
        devContainerId,
        sessionId,
        mode,
        model,
        workspaceId,
        getIdToken,
        messages,
        onThemeGenerated,
        triggerPublish,
      ],
    );

    // Handle connection created from ConnectionSetupHITL card
    const handleConnectionCreated = useCallback(
      async (
        connectionId: string,
        connectorType: string,
        connectionName: string,
      ) => {
        console.log("[AgentMode] Connection created/selected:", {
          connectionId,
          connectorType,
          connectionName,
        });

        // Link connection to project for tracking (fire-and-forget)
        if (projectId) {
          try {
            const token = await getIdToken();
            if (token) {
              await apiClient.post(
                `/api/editor/projects/${projectId}/connections`,
                { connectionId },
                { headers: createAuthHeaders(token) },
              );
              console.log(
                "[AgentMode] Connection linked to project:",
                connectionId,
              );
              onConnectionLinked?.();
            }
          } catch (err: any) {
            // Don't block the agent flow - connection may already be linked
            if (err?.response?.status !== 400) {
              console.warn(
                "[AgentMode] Failed to link connection to project:",
                err?.message,
              );
            }
          }
        }

        if (!socketRef.current || !sessionId) {
          console.warn(
            "[AgentMode] No socket or session for connection continuation",
          );
          return;
        }

        // Fetch the connection to read metadata.selectedResources (set by the
        // resource picker for connectors like google-sheets and excel) so we
        // can name the picked items directly in the continuation prompt.
        let selectedResourcesBlock = "";
        try {
          const token = await getIdToken();
          if (token) {
            const { data: conn } = await apiClient.get<{
              metadata?: {
                selectedResources?: Array<{
                  id: string;
                  name: string;
                  metadata?: Record<string, unknown>;
                }>;
              };
            }>(`/api/connections/${connectionId}`, {
              headers: createAuthHeaders(token),
            });
            const picked = conn?.metadata?.selectedResources ?? [];
            if (picked.length > 0) {
              const lines = picked
                .map((r) => {
                  const driveId = (
                    r.metadata as { driveId?: string } | undefined
                  )?.driveId;
                  return `- ${r.name} (id: ${r.id}${driveId ? `, driveId: ${driveId}` : ""})`;
                })
                .join("\n");
              selectedResourcesBlock = `\n\nSelected resources. Operate on this list:\n${lines}`;
            }
          }
        } catch (err) {
          console.warn("[AgentMode] Could not fetch selectedResources:", err);
        }

        // Send continuation message to agent with connection details
        socketRef.current.emit("start-claude-session", {
          devContainerId,
          prompt: `Connection ready. The user has set up a "${connectorType}" connection.\n\nConnection ID: ${connectionId}\n\nConnection Name: ${connectionName}${selectedResourcesBlock}\n\nProceed with the integration using this connection.`,
          mode,
          model,
          sessionId,
          projectId,
          workspaceId,
        });

        setIsLocalStreaming(true);
        // Placeholder is added by the user-message broadcast handler so it
        // renders below the user message (correct order).
      },
      [
        devContainerId,
        sessionId,
        mode,
        model,
        projectId,
        workspaceId,
        getIdToken,
        onConnectionLinked,
      ],
    );

    // After the user submits a cache TTL in the HITL card, send a
    // continuation prompt so the agent confirms the change in chat.
    const handleCacheTtlSet = useCallback(
      (path: string, ttl: number) => {
        if (!socketRef.current || !sessionId) {
          console.warn(
            "[AgentMode] No socket or session for cache TTL continuation",
          );
          return;
        }
        socketRef.current.emit("start-claude-session", {
          devContainerId,
          prompt: `Cache TTL for "${path}" updated to ${ttl} second${ttl === 1 ? "" : "s"}. Briefly confirm this to the user.`,
          mode,
          model,
          sessionId,
          projectId,
          workspaceId,
        });
        setIsLocalStreaming(true);
      },
      [devContainerId, sessionId, mode, model, projectId, workspaceId],
    );

    // Handle resuming a paused task
    const handleResumeTask = useCallback(
      async (taskId: string) => {
        try {
          const token = await getIdToken();
          if (!token) {
            toast.error("Authentication required");
            return;
          }

          const response = await claudeAgentApi.resumeTask(token, taskId);

          if (response.success) {
            // Clear paused state
            setPausedTask(null);

            // Show streaming state again
            setIsLocalStreaming(true);

            // Update message status back to running
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === taskId
                  ? { ...msg, status: TaskStatus.RUNNING }
                  : msg,
              ),
            );

            toast.success("Task resumed");
            console.log("[AgentMode] Task resumed successfully:", taskId);
          } else {
            throw new Error("Resume failed");
          }
        } catch (error) {
          console.error("[AgentMode] Failed to resume task:", error);
          toast.error("Failed to resume task");
          throw error;
        }
      },
      [getIdToken],
    );

    // Dismiss paused task banner
    const handleDismissPausedTask = useCallback(() => {
      setPausedTask(null);
    }, []);

    return (
      <div
        className={`relative flex flex-col h-full bg-background ${className || ""}`}
      >
        {/* Header: Mode Toggle + Session Selector + Utility Actions */}
        <div className="px-3 py-2.5 border-b border-border flex items-center gap-1.5 bg-card">
          {headerLeftActions}
          {!hideSessionSelector && (
            <div className="flex-1 min-w-0">
              <SessionSelector
                currentSessionId={sessionId}
                onSelectSession={handleSelectSession}
                projectId={projectId || ""}
                disabled={isStreaming}
                refreshTrigger={sessionRefreshTrigger}
              />
            </div>
          )}
          {hideSessionSelector && <div className="flex-1" />}
          {headerRightActions}
          {children}
        </div>

        {/* Preview Test Window — a banner drops in here (right under the header)
            when the agent runs Puppeteer preview tools, and expands to cover the
            chat column (preview stays live). Stop reuses handleStopChat. Runs are
            persisted to localStorage, scoped to this project. */}
        <PreviewTestWindow onStopRun={handleStopChat} projectId={projectId} />

        {/* Connection Status */}
        {!isConnected && (
          <div className="px-3 py-2">
            <Alert className="bg-card border-border">
              <WifiOff className="h-4 w-4 text-muted-foreground" />
              <AlertDescription className="text-muted-foreground">
                Connecting to Claude agent...
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Reconnection Status */}
        {/* Hidden: Reconnecting alert - resumption happens silently in background */}
        {/* {isReconnecting && (
        <div className="px-3 py-2">
          <Alert className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
            <Loader2 className="h-4 w-4 text-blue-600 dark:text-blue-400 animate-spin" />
            <AlertDescription className="text-blue-900 dark:text-blue-100">
              Reconnecting to previous task... {reconnectingTaskId ? `(Task: ${reconnectingTaskId.slice(0, 8)}...)` : ''}
            </AlertDescription>
          </Alert>
        </div>
      )} */}

        {/* BYOC Error Modal - user already switched to credits, just needs to acknowledge */}
        <ByocExpiredModal
          open={showByocModal}
          errorType={(byocError?.errorType as ByocErrorReason) || null}
          title={byocError?.title}
          message={byocError?.message}
          canReconnect={isAdminOrOwner}
          onReconnect={handleByocReconnect}
          onContinue={handleByocContinue}
        />

        {/* Free-plan upgrade modal — reused for the publish gate (default copy) and the
            out-of-credits wall (upsellCopy set by openCreditUpgrade). */}
        <PublishUpsellModal
          isOpen={showPublishUpsell}
          onClose={() => setShowPublishUpsell(false)}
          currentPlanType={PlanType.FREE}
          title={upsellCopy?.title}
          description={upsellCopy?.description}
        />

        {/* Paused Task Recovery Banner */}
        {pausedTask && (
          <div className="px-3 py-2">
            <ErrorRecoveryBanner
              taskId={pausedTask.taskId}
              errorCode={pausedTask.errorCode}
              errorMessage={pausedTask.errorMessage}
              onResume={handleResumeTask}
              onDismiss={handleDismissPausedTask}
            />
          </div>
        )}

        {error && (
          <Alert variant="destructive" className="m-2">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* AgentChat - using div like original AgentModeContainer */}
        <div
          className={`flex-1 min-h-0 flex flex-col${chatContainerClassName ? ` ${chatContainerClassName}` : ""}`}
        >
          <AgentChat
            messages={messages}
            sessionId={sessionId}
            mode={mode}
            model={model}
            onModeChange={handleModeChange}
            onModelChange={handleModelChange}
            onSendPrompt={handleSendPrompt}
            onStopChat={handleStopChat}
            isStreaming={isStreaming}
            disabled={!isConnected}
            onSelectAttach={() => fileInputRef.current?.click()}
            attachedFiles={attachedFiles}
            onRemoveAttachment={(fileName?: string) => {
              if (fileName) {
                setAttachedFiles(
                  attachedFiles.filter((f) => f.file.name !== fileName),
                );
              } else {
                setAttachedFiles([]);
              }
            }}
            onFilesDropped={handleFilesDropped}
            onSelectConnections={onSelectConnections}
            onSelectProjectSettings={onSelectProjectSettings}
            onSelectImageGenerator={onSelectImageGenerator}
            onSelectMediaLibrary={onSelectMediaLibrary}
            onSelectOptisignsAssets={onSelectOptisignsAssets}
            onSelectThemes={onSelectThemes}
            pendingAssetRefs={pendingAssetRefs}
            onPendingAssetRefsConsumed={onPendingAssetRefsConsumed}
            onOpenFile={onOpenFile}
            onAction={handleAction}
            onOpenVisualEditor={onOpenVisualEditor}
            onOpenHistory={onOpenHistory}
            mobileActiveTab={mobileActiveTab}
            onMobileActiveTabChange={onMobileActiveTabChange}
            onOpenSettings={onOpenSettings}
            creditBalance={creditBalance}
            allocatedCredits={allocatedCredits}
            freeDaily={freeDaily}
            onOpenUpgrade={openCreditUpgrade}
            isLoadingCredits={isLoadingCredits}
            shownCreditAlerts={shownCreditAlerts}
            onCreditAlertShown={handleCreditAlertShown}
            byocConnected={byocConnected}
            byocEnabled={byocEnabled}
            isEmailVerificationBlocked={isEmailVerificationBlocked}
            onCheckEmailVerification={handleCheckEmailVerification}
            isCheckingEmailVerification={isCheckingEmailVerification}
            onResendVerificationEmail={handleResendVerificationEmail}
            isResendingVerificationEmail={isResendingVerificationEmail}
            isContainerReady={isContainerReady}
            workspaceId={workspaceId || getActiveWorkspaceId() || ""}
            previewIframe={previewIframe || null}
            previewUrl={previewUrl}
            projectId={projectId}
            devContainerId={devContainerId}
            onEditTheme={onEditTheme}
            onThemeSaved={onThemeSaved}
            onBrowseThemes={onBrowseThemes}
            onConnectionCreated={handleConnectionCreated}
            onCacheTtlSet={handleCacheTtlSet}
            connectionLost={connectionLost}
            onConnectionLostDismiss={() => setConnectionLost(false)}
          />
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTRIBUTE}
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>
    );
  },
);

AgentModeContainer.displayName = "AgentModeContainer";
